import {
    ToastProps,
    ToastType
} from '@honeybook/hbui/dist/components/HBToast/HBToast.types';
import CheckCircleIcon from '@honeybook/icons/dist/icons/CheckCircle24';
import { capitalize, isEqual, isNil, last } from 'lodash';
import colors from '@honeybook/hbui/dist/styles/colors';
import { PartialDeep } from 'type-fest';
import type { RootStore } from '~/stores/root.store';
import { ActionName } from '~/services/actions-manager/action-name';
import {
    Action,
    IBasicCommandsActionValue,
    ServerActionType
} from '~/services/actions-manager/action';
import {
    CommandType,
    CompositionContentType,
    CompositionLayoutType,
    CreateCompositionBlockStages,
    CreateQuestionBlockStages,
    DataHandleMethod,
    IAttributedDupliacteBlockCtx,
    IAttributionContext,
    IBlockBaseContext,
    IBulkUpdateBlockContext,
    ICreateBlockContext,
    ICreateCompositionBlockContext,
    ICreatePackageBlockContext,
    ICreatePackageBlockFromTemplateContext,
    ICreateQuestionBlockContext,
    IDuplicateBlockContext,
    IMoveBlockContext,
    IReplaceBlockContext,
    IReplaceBlockFromClipContext,
    IUpdateBlockColumnsContext,
    IUpdateBlockContext,
    IUpdateServiceSelectionLayoutContext,
    QuestionType,
    ServiceItemScope,
    TBlock,
    TBlockCreationDirection,
    TCommand,
    TScheduleTriggerType,
    TServiceItemBlock
} from '~/types/builder.types';
import { ContainerBlock } from '~/models/blocks/container-block.model';
import {
    BlockTemplateDeleteBlockOptions,
    bulkBlocksUpdate,
    createBlock,
    createCompositionLayoutBlock,
    createMoveBlockCommands,
    createRevertDuplicateBlock,
    createRevertMoveBlockCommands,
    DefaultDeleteBlockOptions,
    deleteBlock,
    doesContainLinkedQuestion,
    duplicateBlock,
    getInvoiceSummaryDefaultsForClientPaymentPlan,
    getNewBlockOrder,
    getParentBlockForCreate,
    IDeleteBlockOptions,
    PasteBlockFromClipboard,
    translateHTMLEntitiesForComparison,
    updateBlockColumnNumber,
    UpdateBlockToTemplate,
    updateServiceSelectionLayout
} from '~/utils/block.utils';
import {
    AmountCalcType,
    CompositionType,
    ContainerableBlockType,
    DateCalcType,
    IChildLayout,
    IImage,
    IInvoiceSummary,
    IService,
    IServiceTemplate,
    ISmartField,
    ServiceItemType,
    ServiceSelectionType,
    ServicesItemsLayoutType,
    TBlockType,
    TCropRatio,
    TInvoiceType,
    TInvoiceTypes
} from '~/types/model.types';
import { getArticlePrefix } from '~/utils/text.utils';
import { QuestionBlock } from '~/models/blocks/question-block.model';
import { ICompositionLayoutStruct } from '~/components/Builder/BuilderSidebar/ContainerSidebar/composition-layout.types';
import { ImageBlock } from '~/models/blocks/image-block.model';
import { getOneToOneRatioImageAttrs } from '~/utils/image.utils';
import { IQuestionTypeConfig } from '~/components/Builder/BuilderSidebar/QuestionSidebar/question-sidebar.utils';
import {
    COMPOSITION_CONFIG_BY_TYPE,
    getDefaultCompositionLayout
} from '~/components/Builder/BuilderSidebar/ContainerSidebar/composition-types-layouts.utils';
import { _last, mergeArrayFields } from '~/utils/lodash.utils';
import { ContainerableBlock } from '~/models/blocks/containerable-block.model';
import {
    calculateBlockBaseDataForServer,
    calculatePageWithAttributionDataForServer,
    CommandsRelation,
    createCreateBlockCommand,
    createDeleteBlockCommand,
    createUpdateBlockCommand,
    extractOldData
} from '~/utils/commands.utils';
import { MultiStageFlowAction } from '~/services/actions-manager/multi-stage-flow-action';
import type { ContractHTMLBlock } from '~/models/blocks/contract-html-block.model';
import {
    QuestionSidebarScreen,
    ImageSidebarScreen
} from '~/components/Builder/BuilderSidebar/builder-sidebar.types';
import {
    esServiceToServiceItem,
    isLastSubItemOnPackage
} from '~/components/Builder/Block/ServicesBlock/service-block.utils';
import {
    FlowApplyActionsErrorsTypes,
    IServiceResponse,
    ISessionConfig,
    TESServiceOrPackage
} from '~/types/server.types';
import type { ServiceItemBlock } from '~/models/blocks/service-item-block.model';
import { FlowActionGroup } from '~/services/actions-manager/FlowActionGroup';
import { FlowAction } from '~/services/actions-manager/flow-action';
import type { ServiceSelectionBlock } from '~/models/blocks/service-selection-block.model';
import { delayedScrollToBlock, scrollToBlock } from '~/utils/flow.utils';
import { ServiceSelectionItemBlock } from '~/models/blocks/service-selection-item-block.model';
import { getServiceItemOverridableProps } from '~/components/common/InvoiceBlock/invoice.utils';
import type { InvoiceBlock } from '~/models/blocks/invoice-block.model';
import type { CompanyInfoBlock } from '~/models/blocks/company-info-block.model';
import {
    DEFAULT_COMPANY_INFO_LAYOUT,
    getCompanyInfoLayouts
} from '~/components/Builder/BuilderSidebar/ContainerSidebar/company-info-layout.utils';
import { InvoiceServiceItemBlock } from '~/models/blocks/invoice-service-item-block.model';
import { IAddBlockProps } from '~/components/Builder/CreateBlock/AddBlock/useOnAddBlock';
import { TextBlock } from '~/models/blocks/text-block.model';
import { CoreOutboundEvents } from '~/components/Builder/Mobile/builderMobileApi.enums';
import { actionBlockTypes } from '~/common/flows.constants';
import {
    checkLimitations,
    reuseBlockCreationLimitations
} from '~/components/Builder/Common/CreationLimitations/creationLimitations';
import { PaymentScheduleBlock } from '~/models/blocks/payment-schedule-block.model';
import { ServicesBlock as ServicesBlockModel } from '~/models/blocks/services-block.model';
import { ISelectedServiceValue } from '~/components/ClientExperience/OneFlow/FlowContent/CXServiceSelectionBlock/CXServiceSelection.types';
import { ScheduleContainerBlock } from '~/models/blocks/schedule-container-block.model';
import { TFuncKey } from 'react-i18next';
import type { ICreatePageActionProps } from '~/controllers/page.controller';
import i18next, { getFixedT } from 'i18next';
import { boundMethod } from 'autobind-decorator';
import { NAMESPACES } from '~/i18n/namespaces';
import type { LeadDetailsBlock } from '~/models/blocks/lead-details-block.model';
import { BlockLike } from '~/components/Builder/Common/CreationLimitations/creationLimitations.types';
import type { PaymentBlock } from '~/models/blocks/payment-block.model';
import type { Flow } from '~/models/flow.model';
import { getBannerDefaultText } from '~/components/Builder/Block/ServiceSelectionBlock/service-selection-block.utils';
import type { Page } from '~/models/page.model';
import useAnalytics from '~/common/useAnalytics';
import { AnalyticsEvents } from '~/services/analytics/analytics-events';
import { forbidMoreThanMaxUploadQuestionBlocksForCompositionBlock } from '~/components/Builder/Common/CreationLimitations/questionLimitations';
import { InvoiceController } from '~/controllers/invoice.controller';
import { trackError } from '~/services/logger/error.utils';

export interface BlockAndNewBlockId {
    block?: TBlock;
    createdBlockId: string;
}

export type CreateBlockSideEffectFunction = {
    ({ block, createdBlockId }: BlockAndNewBlockId): void;
};

export interface ICreateBlockOptions {
    invoiceToast?: ToastProps['text'];
    invoiceToastTfuncKey?: TFuncKey;
    sideEffectCallback?: CreateBlockSideEffectFunction;
    shouldSelectBlock?: boolean;
    applyResolvedCallback?: () => void;
    compositionLayoutType?: CompositionLayoutType;
}

export interface ICreatePageAndBlockProps<T extends TBlockType> {
    blockType?: T;
    initialAttributes?: Partial<Extract<TBlock, { type: T }>>;
    pageOptions: ICreatePageActionProps;
    blockOptions?: ICreateBlockOptions;
}

interface ICreateBlockProps {
    block: TBlock;
    blockType: TBlockType;
    direction: TBlockCreationDirection;
    compositionType?: CompositionType;
    // is the passed block the parent to create under, or the sibling
    isParent?: boolean;
    initialAttributes?: any;
    layout?: ICompositionLayoutStruct;
    isInAppBrowser?: boolean;
    isFromPaste?: boolean;
    options?: ICreateBlockOptions;
}

interface ICreateCreateBlockActionProps {
    block: TBlock;
    blockType: TBlockType;
    order: number;
    initialAttributes?: any;
    layout?: ICompositionLayoutStruct;
    compositionType?: CompositionType;
    options?: ICreateBlockOptions;
}

interface ICreateBlockCommandsOptions {
    initialAttributes?: any;
    layout?: ICompositionLayoutStruct;
    parent?: TBlock;
}

export interface IUpdateBlockOptions {
    oldData?: object;
    updateDataHandleMethod?: DataHandleMethod;
    applyResolvedCallback?: () => void;
    applySideEffectsCallback?: () => void;
    revertSideEffectsCallback?: () => void;
}

export interface IBlockActionDescriptionProps {
    actionType: string;
    blockType?: TBlockType;
    compositionType?: string;
    isFromPaste?: boolean;
    origin?: string;
    compositionContentType?: CompositionContentType;
}

export interface IPasteFromClipboardToTempBlockProps {
    blockType: TBlockType;
    block: TBlock;
    compositionType: CompositionType;
    addBlock: (props: IAddBlockProps) => void;
}

export const UpdateBlockOptionsDefaults = {
    updateDataHandleMethod: DataHandleMethod.merge
};

export interface IFindBlockBy {
    blockId?: string;
    blockType?: TBlockType;
}

export interface IUpdateTaxFieldProps {
    serviceItemBlock: ServiceItemBlock;
    value: boolean;
    propName: string;
}

export class BlockController {
    rootStore: RootStore;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
    }

    // #region actions
    updateBlock<Block extends TBlock>(
        // This was added in this PR: https://github.com/HoneyBook/honeybook-react/pull/1977
        // We need to ability to pass only block_id to updateBlock, because passing block causes useEffects/useCallbacks
        // to be called multiple times unnecessarily.
        { block_id, flowRevision }: Block | Partial<Block>,
        data: PartialDeep<Block>,
        options: IUpdateBlockOptions = UpdateBlockOptionsDefaults
    ) {
        const {
            actionsManager,
            builderUiStore: { selectedFlow, selectedFlowId }
        } = this.rootStore;
        // We take the revision from the block (if the entire block was passed)
        // and if only a block_id was passed we fallback to the draft or active revision
        let revision;
        try {
            revision = flowRevision || selectedFlow.flowRevision;
        } catch (e) {
            trackError({
                error: e,
                message: 'undefined selectFlow error',
                context: {
                    block_id,
                    selectedFlowId
                }
            });
            throw e;
        }

        const block = revision?.blocksMap[block_id];

        // Protect against a case where trying to update but it has already been deleted
        if (!block) {
            return;
        }

        const updateBlockAction = this.createUpdateBlockAction(
            block,
            data,
            options
        );

        actionsManager.doNewApplyAction(updateBlockAction);
    }

    updateQuestionBlock(
        block: QuestionBlock,
        data: object,
        oldData: object = undefined
    ) {
        const { question, ...rest } = data as Partial<QuestionBlock>;
        const dataWithType = {
            question: {
                question_type:
                    block.question.question_type || QuestionType.ShortText,
                ...question
            },
            ...rest
        };

        const oldDataForUndo = oldData && {
            question: (oldData as Partial<QuestionBlock>).question,
            ...rest
        };

        // @ts-ignore
        this.updateBlock(block, dataWithType, { oldData: oldDataForUndo });
    }

    updateContractMetadataBlock(
        block: ContractHTMLBlock,
        fieldMetaData: ISmartField
    ) {
        const metadata = (
            block as ContractHTMLBlock
        ).contract.fields_metadata.map(field =>
            field.field_id === fieldMetaData.field_id
                ? {
                      ...field,
                      default_value: fieldMetaData.default_value,
                      fill_type: fieldMetaData.fill_type,
                      placeholder: fieldMetaData.placeholder,
                      name: fieldMetaData.name,
                      data_source_property: fieldMetaData.data_source_property,
                      data_source_sub_property:
                          fieldMetaData.data_source_sub_property,
                      data_source_type: fieldMetaData.data_source_type
                  }
                : field
        );

        this.updateBlock(block, {
            contract: {
                html_body: (block as ContractHTMLBlock).contract.html_body,
                fields_metadata: metadata
            }
        });
    }

    updateSingleServiceItemField = ({
        field,
        value,
        block
    }: {
        field: string;
        value: boolean | string;
        block: ServiceItemBlock;
    }) => {
        if (block.isInvoiceOrPayBlock) {
            block.invoiceBlock.invoiceSummaryBlock.setIsVariablesUpdated(false);
        }

        const updatedData: any = {
            service_item: {
                ...block.service_item,
                [field]: value
            }
        };

        if (
            block.isServiceSelectionItemBlock &&
            (block as ServiceSelectionItemBlock).invoice_service_item
        ) {
            updatedData.invoice_service_item = {
                ...(block as ServiceSelectionItemBlock).invoice_service_item,
                [field]: value
            };
        }

        this.updateBlock(block, updatedData, {
            applyResolvedCallback: () => {
                if (block.isInvoiceOrPayBlock) {
                    block.invoiceBlock.invoiceSummaryBlock.setIsVariablesUpdated(
                        true
                    );
                }
            }
        });
    };

    updateImageBlock = (block: ImageBlock, image: IImage) => {
        // If this is a circled image/placeholder or 1:1 ratio - keep the shape for the next image
        let imageAttrs = image;
        const currentImage = block.image;
        const isOneToOneRatio = // Circle is also one to one - same width and height.
            currentImage?.crop_ratio === 'Circle' ||
            currentImage?.crop_ratio === '1:1';

        if (currentImage && isOneToOneRatio) {
            imageAttrs = getOneToOneRatioImageAttrs(image, currentImage);
        }

        this.updateBlock(block, { image: imageAttrs });
    };

    updateServiceItemBlock = (
        block: TServiceItemBlock,
        serviceItem: Partial<IService>,
        opts = {},
        scope?: ServiceItemScope
    ) => {
        // Tests for this monstrosity can be found in BaseServiceItemBlock.test.tsx
        // Please feel welcome to add tests there as this logic feels pretty fragile
        const isServiceSelectionItemBlock =
            block instanceof ServiceSelectionItemBlock;
        const isInvoiceServiceItemBlock =
            block instanceof InvoiceServiceItemBlock;
        const shouldSyncInvoiceServiceItem =
            isServiceSelectionItemBlock && scope === ServiceItemScope.Invoice;
        const currentServiceItem = shouldSyncInvoiceServiceItem
            ? (block as ServiceSelectionItemBlock).invoice_service_item
            : block.service_item;
        const isOverridablePropsChange = block.compareOverridableProps(
            serviceItem,
            currentServiceItem ?? block.service_item
        );

        if (isEqual(currentServiceItem, serviceItem)) {
            return;
        }

        let updateData = {};

        if (isInvoiceServiceItemBlock) {
            updateData = {
                service_item: {
                    ...serviceItem
                }
            };

            this.updateBlock(block, updateData, opts);
            return;
        }

        const serviceItemBlock = block as ServiceSelectionItemBlock;

        const isSubItemTitleEmptyInSs =
            block.isInPackage && !serviceItemBlock.service_item.title;

        if (!isOverridablePropsChange) {
            updateData = this.getNonOverridablePropsData(
                block,
                serviceItem,
                serviceItemBlock
            );
        } else {
            // update overridable props

            if (shouldSyncInvoiceServiceItem) {
                if (isSubItemTitleEmptyInSs) {
                    // This prevents a deadlock by copying a title given to the sub-item from the invoice scope to the ssb
                    updateData = {
                        service_item: {
                            ...serviceItem
                        },
                        invoice_service_item: {
                            ...serviceItem
                        }
                    };
                } else {
                    updateData = {
                        invoice_service_item: {
                            ...serviceItem
                        }
                    };
                }
            } else {
                updateData = {
                    service_item: {
                        ...serviceItem
                    }
                };
            }
        }

        this.updateBlock(block, updateData, opts);
    };

    async getContractFields(flowId: string) {
        const { contractService, builderUiStore } = this.rootStore;
        const optionalFields = await contractService.fetchContractFields(
            flowId
        );
        builderUiStore.setOptionalFields(optionalFields);
        return optionalFields;
    }

    updateCompositionLayout(
        block: TBlock,
        compositionType: CompositionType,
        layoutStruct: ICompositionLayoutStruct
    ) {
        const { actionsManager, builderUiStore } = this.rootStore;
        const order = getNewBlockOrder(block, 'bottom');

        const parentBlockId = block.parent_id;
        const pageId = builderUiStore.selectedPageId;
        const action =
            actionsManager.getActionByIdParam<ICreateCompositionBlockContext>({
                blockId: parentBlockId,
                pageId,
                actionName: ActionName.CreateCompositionBlock
            });

        action.setContext({
            blockId: block.block_id
        });

        actionsManager.applyStageForAction(
            action,
            CreateCompositionBlockStages.deleteCurrentLayout
        );

        action.setContext({
            blockId: parentBlockId,
            order,
            layout: layoutStruct
        });

        actionsManager.applyStageForAction(
            action,
            CreateCompositionBlockStages.createCompositionLayout
        );
    }

    toggleClientPaymentPlan = (isOn: boolean) => {
        const {
            invoiceSummaryBlock,
            invoiceSummaryBlock: { isClientPaymentPlan }
        } = this.rootStore.builderUiStore.selectedFlow;

        this.updateBlock(invoiceSummaryBlock, {
            invoice_summary: {
                ...(isNil(isClientPaymentPlan) &&
                    getInvoiceSummaryDefaultsForClientPaymentPlan()),
                is_client_payment_plan: isOn
            }
        });
    };

    toggleCompanyInfoBlockLink(block: CompanyInfoBlock) {
        const {
            actionsManager,
            builderUiStore: { selectBlock },
            companyStore: { currCompany }
        } = this.rootStore;

        const {
            order,
            parent,
            company_info_layout_type,
            linked_to_company_settings
        } = block;

        if (linked_to_company_settings) {
            actionsManager.groupActions(() => ({
                name: ActionName.UpdateMultipleBlocks,
                serverActionType: ServerActionType.UpdateBlock,
                groupActions: () => {
                    this.updateBlock(block, {
                        linked_to_company_settings: false
                    });
                    block.allDescendants().forEach(childBlock => {
                        // The child blocks content is already updated locally with the company info
                        // so we just need to send it to the server as it is.
                        // (search "setCompanyInfoOnDescendants")
                        if (childBlock instanceof ImageBlock) {
                            this.updateBlock(childBlock, {
                                image: childBlock.image
                            });
                        } else if (childBlock instanceof TextBlock) {
                            this.updateBlock(childBlock, {
                                content: childBlock.content
                            });
                        }
                    });
                }
            }));
        } else {
            actionsManager.groupActions(action => ({
                name: ActionName.ReplaceBlock,
                serverActionType: ServerActionType.ReplaceBlock,
                applySideEffects: () =>
                    selectBlock(
                        (action.groupedActions[1].ctx as ICreateBlockContext)
                            .newTargetBlock.block_id
                    ),
                revertSideEffects: () => selectBlock(block.block_id),
                groupActions: () => {
                    this.deleteBlock(block);

                    const createBlockAction = this.createCreateBlockAction({
                        block: parent,
                        order,
                        blockType: 'company_info_block',
                        compositionType: CompositionType.CompanyInfo,
                        layout: {
                            ...getCompanyInfoLayouts(currCompany).layouts[
                                company_info_layout_type ||
                                    DEFAULT_COMPANY_INFO_LAYOUT
                            ].layoutStruct,
                            linked_to_company_settings: true
                        }
                    });
                    actionsManager.doNewApplyAction(createBlockAction);
                }
            }));
        }
    }

    getBlockActionDescription({
        actionType,
        blockType,
        compositionType,
        isFromPaste,
        origin,
        compositionContentType
    }: IBlockActionDescriptionProps) {
        let description = `${capitalize(actionType)}d`;

        if (compositionContentType) {
            if (compositionContentType !== 'composition') {
                const compositionContentTypeName = `${getArticlePrefix(
                    compositionContentType
                )} ${compositionContentType.replace(/_/g, ' ')}`;

                description += ` ${compositionContentTypeName} block under a composition block`;
            } else {
                description += ' a composition block';
            }

            if (compositionType) {
                description += ` of type ${compositionType}`;
            }
        } else {
            const blockTypeName = ` ${getArticlePrefix(
                blockType
            )} ${blockType.replace(/_/g, ' ')}`;

            description += blockTypeName;

            if (compositionType) {
                description += ` of composition type ${compositionType.replace(
                    /_/g,
                    ' '
                )}`;
            }
        }

        description += isFromPaste ? ' with copy paste.' : ' manually.';

        if (origin) {
            description += ` Initiated from ${origin}.`;
        }

        return description;
    }

    createBlock({
        block,
        blockType,
        direction,
        compositionType,
        initialAttributes,
        layout,
        isParent = false, // is the passed block the parent to create under, or the sibling
        options = {}
    }: ICreateBlockProps): string {
        const { actionsManager, pageCtrl, toastStore } = this.rootStore;
        const blockToCreateUnder = getParentBlockForCreate(block, isParent);
        const order = getNewBlockOrder(block, direction, isParent);

        if (compositionType) {
            const action = this.createCompositionCreateBlockAction({
                block: blockToCreateUnder,
                blockType,
                compositionContentType:
                    initialAttributes?.compositionContentType,
                compositionType,
                layout,
                order,
                origin: initialAttributes?.origin,
                compositionLayoutType: initialAttributes?.compositionLayoutType,
                initialAttributes
            });

            actionsManager.applyStageForAction(
                action,
                CreateCompositionBlockStages.createDefaultBlock
            );

            return;
        }

        if (blockType === 'question_block') {
            const action = this.createQuestionCreateBlockAction(
                blockToCreateUnder,
                blockType,
                order,
                options,
                initialAttributes
            );

            actionsManager.applyStageForAction(
                action,
                CreateQuestionBlockStages.createDefaultBlock
            );

            return action.ctx?.newTargetBlock?.block_id;
        }

        if (blockType === 'invoice_container_block') {
            const action = pageCtrl.createInvoiceBlockAction(
                blockToCreateUnder,
                order,
                initialAttributes,
                false,
                options
            );

            action.description = this.getBlockActionDescription({
                actionType: 'create',
                blockType
            });

            actionsManager.doNewApplyAction(action);

            const text =
                options.invoiceToast || i18next.t(options.invoiceToastTfuncKey);
            if (text) {
                toastStore.add({
                    type: ToastType.Info,
                    icon: false,
                    text,
                    autoDismissMs: 12000
                });
            }

            return;
        }

        if (blockType === 'schedule_container_block') {
            const shouldShowReorderBanner =
                this.isCreatingBlockAfterLinkedContract(
                    blockToCreateUnder,
                    'session'
                );

            if (shouldShowReorderBanner) {
                toastStore.add({
                    type: ToastType.Info,
                    icon: false,
                    text: i18next.t(
                        'builder:contract.smart_fields.toasts.session_smart_fields.scheduler_after_contract.text'
                    ),
                    autoDismissMs: 12000
                });
            }
        }

        const createBlockAction = this.createCreateBlockAction({
            block: blockToCreateUnder,
            blockType,
            order,
            layout,
            initialAttributes,
            options
        });

        actionsManager.doNewApplyAction(createBlockAction);

        return createBlockAction.ctx?.newTargetBlock?.block_id;
    }

    pasteFromClipboardToTempBlock({
        blockType,
        block,
        compositionType,
        addBlock
    }: IPasteFromClipboardToTempBlockProps) {
        const { actionsManager, builderUiStore, pageCtrl } = this.rootStore;

        // if copied block is containeralble, and block to be pasted under is not a containerable
        // or it is a child of action block - paste to root block
        const shouldUseRootLevelBlock =
            (ContainerableBlockType[blockType] &&
                !ContainerableBlockType[block.type]) ||
            block.calculateRootLevelParent()?.isActionBlock;

        actionsManager.groupActions((action: FlowActionGroup) => ({
            name: ActionName.PasteBlock,
            description: this.getBlockActionDescription({
                actionType: 'create',
                blockType,
                isFromPaste: true
            }),
            serverActionType: ServerActionType.UpdateBlockToTemplate,
            applySideEffects: () => {},
            createApplyDataForServer: () =>
                this.getAttributedDataForServer(
                    action.actionAs<FlowAction<IAttributionContext>>()
                ),
            onApplyToServerResolved: (result, { variables: newVariables }) =>
                this.addBlockVariables(action, newVariables),
            onRevertToServerResolved: () => this.removeBlockVariables(action),
            groupActions: () => {
                let pasteOnBlock = shouldUseRootLevelBlock
                    ? block.calculateRootLevelParent()
                    : block;

                if (builderUiStore.isInNewPageScreen) {
                    pageCtrl.createPage();
                    pasteOnBlock = builderUiStore.selectedPage.rootBlock;
                }

                addBlock({
                    blockType,
                    parent: pasteOnBlock,
                    compositionType,
                    isFromPaste: true
                });

                const doesHaveCompositionType = !!compositionType;
                this.pasteBlockFromClipboard(doesHaveCompositionType);
            }
        }));
    }

    deleteBlock(
        block: TBlock,
        options?: { applyResolvedCallback?: () => void }
    ) {
        const { actionsManager } = this.rootStore;
        if (
            block.isServiceSelectionItemBlock &&
            !(block as ServiceSelectionItemBlock).isSubItem &&
            (block as ServiceSelectionItemBlock).isServiceSelected
        ) {
            const serviceSelectionBlock = (block as ServiceSelectionItemBlock)
                .serviceSelectionBlock;
            this.deleteBlockAndUpdateVariable(block, serviceSelectionBlock, {
                selected_services: (
                    serviceSelectionBlock.connectedVariable?.value
                        ?.selected_services as ISelectedServiceValue[]
                ).filter(
                    service => service.service_item_block_id !== block.block_id
                )
            });
        } else if (isLastSubItemOnPackage(block)) {
            // delete Last Service Item Block and update parent to be Service Item Type
            this.deleteAndUpdateParentBlock(block, {
                service_type: ServiceItemType.Service
            });
        } else {
            const deleteBlockActions = this.createDeleteBlockAction(
                block,
                options
            );
            actionsManager.doNewApplyAction(deleteBlockActions);
        }
    }

    updateBlockColumns(block: ContainerBlock, nColumns: number) {
        const { actionsManager } = this.rootStore;

        const updateBlockColumnsAction = this.createUpdateBlockColumnsAction(
            block,
            nColumns
        );

        actionsManager.doNewApplyAction(updateBlockColumnsAction);
        return updateBlockColumnsAction;
    }

    updateLeadDetailsBlockTwoColumns(block: LeadDetailsBlock) {
        const { actionsManager } = this.rootStore;

        actionsManager.groupActions(() => ({
            name: ActionName.UpdateBlockColumns,
            serverActionType: ServerActionType.UpdateBlockColumns,
            groupActions: () => {
                const {
                    ctx: { createdVerticalContainers }
                } = this.updateBlockColumns(block, 2);

                const { clientEmailQuestionBlock } = block;
                this.moveBlock(
                    clientEmailQuestionBlock,
                    createdVerticalContainers[0],
                    0
                );
            }
        }));
    }

    updateServiceSelectionLayout(
        block: ServiceSelectionBlock,
        newLayout: ServicesItemsLayoutType
    ) {
        const { actionsManager } = this.rootStore;

        const updateServiceSelectionLayoutAction =
            this.createUpdateServiceSelectionLayoutAction(block, newLayout);

        actionsManager.doNewApplyAction(updateServiceSelectionLayoutAction);
    }

    updateMultipleBlocks(
        block: ContainerBlock,
        oldBlocksData: Array<Partial<TBlock>>,
        newBlocksData: Array<Partial<TBlock>>
    ) {
        const { actionsManager } = this.rootStore;

        const updateMultipleBlocksAction =
            this.createUpdateMultipleBlocksAction(
                block,
                oldBlocksData,
                newBlocksData
            );

        actionsManager.doNewApplyAction(updateMultipleBlocksAction);
    }

    moveBlock(block: TBlock, parent: ContainerableBlock, order: number) {
        const { actionsManager } = this.rootStore;

        const moveBlockAction = this.createMoveBlockAction(
            block,
            parent,
            order
        );
        actionsManager.doNewApplyAction(moveBlockAction);
    }

    createService({ block }: { block: ServicesBlockModel }): void {
        const { eventEmitterCtrl } = this.rootStore;

        // createBlock supports two ways to tell it where to put the new block. Either passing a sibling, or a parent
        // If block (IE - ServicesBlock) has children, use the last child (sibling) as the reference block
        // If not, use the ServicesBlock as the reference block (parent) and set isParent to true
        const createAttrs = block.childBlocks.length
            ? {
                  block: last(block.childBlocks)
              }
            : {
                  block,
                  isParent: true
              };

        // on invoice service item creation on mobile - we don't select the block after creation
        // since it's not selectable. We just need to scroll to the block. So we pass this callback
        // to be executed, to scroll to block and stop the rest of the execution
        const onSideEffectCallBack = ({
            createdBlockId
        }: {
            createdBlockId: string;
        }) => {
            return eventEmitterCtrl.isMobile
                ? delayedScrollToBlock({
                      blockId: createdBlockId,
                      isMobileApp: true
                  })
                : () => {};
        };

        this.createBlock({
            ...createAttrs,
            blockType: 'invoice_service_item_block',
            direction: 'bottom',
            options: {
                shouldSelectBlock: !eventEmitterCtrl.isMobile,
                sideEffectCallback: onSideEffectCallBack
            }
        });
        return;
    }

    updateInvoiceServiceItemBlock({
        block,
        serviceItem,
        scope
    }: {
        block: TServiceItemBlock;
        serviceItem: Partial<IService>;
        scope: ServiceItemScope;
    }) {
        block.invoiceBlock?.invoiceSummaryBlock.setIsVariablesUpdated(false);
        this.updateServiceItemBlock(
            block,
            serviceItem,
            {
                applyResolvedCallback: () => {
                    block.invoiceBlock?.invoiceSummaryBlock.setIsVariablesUpdated(
                        true
                    );
                }
            },
            scope
        );
    }

    createPackageAndItems(
        block: TBlock,
        services: IServiceResponse[],
        item: TESServiceOrPackage,
        scope: ServiceItemScope,
        serviceImage: IImage
    ): IService {
        const { actionsManager } = this.rootStore;

        const createPackageAction = this.createPackageItemsBlockAction({
            block,
            services,
            item,
            scope,
            serviceImage
        });

        actionsManager.doNewApplyAction(createPackageAction);

        return createPackageAction.ctx.updatedServiceItem;
    }

    createPackageAndItemsFromTemplate(
        block: TBlock,
        item: IServiceTemplate,
        scope: ServiceItemScope,
        withTaxes = false
    ): IService {
        const { actionsManager } = this.rootStore;

        const createPackageAction =
            this.createPackageItemsBlockFromTemplateAction({
                block,
                item,
                scope,
                serviceImage: item.image,
                withTaxes
            });

        actionsManager.doNewApplyAction(createPackageAction);

        return createPackageAction.ctx.updatedServiceItem;
    }

    duplicateBlock(block: TBlock) {
        const { actionsManager, toastStore } = this.rootStore;

        const duplicateBlockAction = this.createDuplicateBlockAction(block);

        actionsManager.doNewApplyAction(duplicateBlockAction);

        // side effects after block duplication:

        // Get the id of the newly duplicated block (it is the last child being updated in the parent's children array)

        const duplicatedBlockId = _last(
            (
                duplicateBlockAction.ctx.commands.find(
                    cmd => cmd.type === CommandType.Update
                ).data as ContainerBlock
            )?.children
        )?.block_id;

        this.rootStore.flowCtrl.selectBlock({ blockId: duplicatedBlockId });

        // If duplicated block was/contains a linked question, show toast
        if (doesContainLinkedQuestion(block)) {
            toastStore.add({
                type: ToastType.Info,
                text: 'The duplicated block contained linked questions. Please note that we have copied these questions to the new block  - but unlinked them',
                autoDismissMs: 12000
            });
        }
    }

    copyBlockToClipboard(isCut: boolean = false) {
        const {
            toastStore,
            clipboardStore: { setCopiedBlock },
            builderUiStore: { selectedBlock },
            actionsManager
        } = this.rootStore;

        const t = getFixedT(null, NAMESPACES.BUILDER, 'cut');
        const blockToCopy = selectedBlock;

        if (!blockToCopy) {
            toastStore.add({
                type: ToastType.Error,
                text: `This element cannot be copied. Please select an element or composition instead`,
                autoDismissMs: 12000
            });

            return;
        }

        if (blockToCopy.calculateRootLevelParent()?.isActionBlock) {
            toastStore.add({
                type: ToastType.Error,
                text: `This element cannot be copied. Try copying the whole element instead`,
                autoDismissMs: 12000
            });

            return;
        }

        if (
            blockToCopy.isChargeContainerBlock ||
            blockToCopy.calculateRootLevelParent()?.isChargeContainerBlock
        ) {
            toastStore.add({
                type: ToastType.Error,
                text: `You can't copy a Payment block. Did you mean to copy the Invoice?`,
                autoDismissMs: 12000
            });

            return;
        }

        if (blockToCopy.isCutDisabled && isCut) {
            toastStore.add({
                type: ToastType.Error,
                text: t(blockToCopy.type as any),
                autoDismissMs: 6000
            });

            return;
        }

        if (
            blockToCopy.isLeadDetailsBlock() ||
            (blockToCopy instanceof QuestionBlock &&
                blockToCopy.isLeadDetailsQuestion)
        ) {
            // this prevents copy/ cut via keyboard shortcut
            toastStore.add({
                type: ToastType.Error,
                text: `You can't copy or cut a contact info block`,
                autoDismissMs: 6000
            });

            return;
        }

        setCopiedBlock(blockToCopy);

        const toast: ToastProps = {
            type: ToastType.CustomInfo,
            text: `Block ${isCut ? 'cut' : 'copied'} successfully`,
            autoDismissMs: 5000,
            icon: CheckCircleIcon,
            iconColor: colors.neutral900,
            customColor: colors.blue20,
            actionLink: undefined
        };

        if (isCut) {
            this.deleteBlock(blockToCopy);

            const actionLink = {
                text: 'UNDO',
                onClick: () => {
                    actionsManager.undoAction();
                }
            };

            toast.actionLink = actionLink;
        }

        toastStore.add(toast);
    }

    @boundMethod
    updateTaxField({
        serviceItemBlock,
        value,
        propName
    }: IUpdateTaxFieldProps) {
        serviceItemBlock.invoiceBlock.invoiceSummaryBlock.setIsVariablesUpdated(
            false
        );

        const updatedData: any = {
            service_item: {
                ...serviceItemBlock.service_item,
                [propName]: value
            }
        };

        if (
            serviceItemBlock.isServiceSelectionItemBlock &&
            (serviceItemBlock as ServiceSelectionItemBlock).invoice_service_item
        ) {
            updatedData.invoice_service_item = {
                ...(serviceItemBlock as ServiceSelectionItemBlock)
                    .invoice_service_item,
                [propName]: value
            };
        }

        this.updateBlock(serviceItemBlock, updatedData, {
            applyResolvedCallback: () => {
                serviceItemBlock.invoiceBlock.invoiceSummaryBlock.setIsVariablesUpdated(
                    true
                );
            }
        });
    }

    updateInvoiceSummary(updates: Partial<IInvoiceSummary>) {
        const block =
            this.rootStore.builderUiStore.selectedFlow.invoiceSummaryBlock;

        block.setIsVariablesUpdated(false);
        const data = {
            invoice_summary: {
                ...block.invoice_summary,
                ...updates
            }
        };

        this.updateBlock(block, data, {
            applyResolvedCallback: () => {
                block.setIsVariablesUpdated(true);
            }
        });
    }

    toggleTaxOnInvoice({
        block,
        taxNumber,
        active,
        label,
        invoiceCtrl
    }: {
        invoiceCtrl: InvoiceController;
        block: InvoiceBlock;
        taxNumber: string;
        active: boolean;
        label?: string;
    }) {
        const { invoiceSummaryBlock } = block;

        invoiceSummaryBlock.setIsVariablesUpdated(false);

        const defaultProperties = {
            value: 0,
            type: 'relative'
        };

        this.rootStore.actionsManager.groupActions(() => ({
            name: ActionName.UpdateBlockData,
            serverActionType: ServerActionType.UpdateBlock,
            groupActions: () => {
                this.updateInvoiceSummary({
                    [taxNumber]: {
                        ...invoiceSummaryBlock.invoice_summary[taxNumber],
                        active,
                        label,
                        ...(!invoiceSummaryBlock.invoice_summary[taxNumber]
                            ?.value && defaultProperties)
                    }
                });

                // Show / hide tax column
                this.updateBlock(block.invoiceColumnHeaderBlock, {
                    styles: {
                        [`show_${taxNumber}`]: active
                    }
                });

                // Turn off all service item descendants
                if (!active) {
                    invoiceCtrl.updateAllServiceItemsTaxes({
                        block: block.invoiceColumnHeaderBlock,
                        value: false,
                        taxNumber
                    });
                }
            }
        }));
    }

    updateFirstPaymentToToday(flow: Flow) {
        const firstPayment = flow.paymentScheduleBlock.firstPayment;
        this.updateBlock<PaymentBlock>(
            {
                block_id: firstPayment.block_id,
                flowRevision: flow.flowRevision
            },
            {
                payment_content: {
                    date_calc_type: DateCalcType.Today,
                    amount_calc_type: AmountCalcType.DivideEqually
                }
            }
        );
    }

    onlyKeepFirstPayment(flow: Flow) {
        const firstPaymentId = flow.paymentScheduleBlock.firstPayment.block_id;
        const restOfPayments = flow.paymentsBlocks.filter(
            blk => blk.block_id !== firstPaymentId
        );

        restOfPayments.forEach(payment => {
            this.deleteBlock(payment);
        });
    }

    changeInvoiceForPublicFlow(flow: Flow) {
        const { builderUiStore } = this.rootStore;

        const isRecurringInvoice =
            flow.invoiceContainerBlock.isRecurringInvoice;

        if (isRecurringInvoice) {
            this.toggleInvoiceType(
                flow.invoiceContainerBlock,
                TInvoiceTypes.PaymentSchedule,
                true
            );
        }

        this.updateFirstPaymentToToday(flow);
        this.onlyKeepFirstPayment(flow);

        builderUiStore.setChangedPaymentScheduleDialogProps({
            isOpen: true,
            isRecurringInvoice
        });
    }

    isCreatingBlockAfterLinkedContract(
        blockToCreateUnder: TBlock,
        sourceType: string
    ) {
        const contracts = blockToCreateUnder?.flow.contractHtmlBlocks;

        if (!contracts?.length) {
            return false;
        }

        // either contract block doesnt have related smart fields,
        // or it is before the block to create under
        return contracts.every(contract =>
            contract.hasSmartFieldsWithSourceType(sourceType)
                ? blockToCreateUnder.page.isAfterPage(contract.page)
                : false
        );
    }

    changePaymentsDueToday(flow: Flow) {
        const paymentsDueToday: PaymentBlock[] = flow.paymentsBlocks.filter(
            blk => blk.dateCalcType === DateCalcType.Today
        );
        if (paymentsDueToday.length) {
            paymentsDueToday.forEach(blk => {
                this.updateBlock<PaymentBlock>(
                    {
                        block_id: blk.block_id,
                        flowRevision: flow.flowRevision
                    },
                    {
                        payment_content: {
                            date_calc_type: DateCalcType.DateSent,
                            amount_calc_type: AmountCalcType.DivideEqually
                        }
                    }
                );
            });
        }
    }

    pasteBlockFromClipboard(isBaseBlockAContainerBlock: boolean) {
        // assumes that a new block was created to be replaced before calling this function
        // this will replace selectedBlock with copiedBlock
        const {
            toastStore,
            clipboardStore: {
                copiedBlock,
                allBlocks,
                setDidPasteFromClipboard
            },
            actionsManager,
            builderUiStore: {
                selectedBlock,
                selectedFlow,
                resetOverlays,
                selectAndScrollToBlock
            }
        } = this.rootStore;

        // disable automatically made overlays when pasting blocks (no need to choose template)
        resetOverlays();

        actionsManager.groupActions((action: FlowActionGroup) => ({
            name: ActionName.PasteBlock,
            description: this.getBlockActionDescription({
                actionType: 'create',
                blockType: copiedBlock?.type,
                isFromPaste: true
            }),
            serverActionType: ServerActionType.UpdateBlockToTemplate,
            applySideEffects: () => {},
            onApplyToServerResolved: (result, { variables: newVariables }) =>
                this.addBlockVariables(action, newVariables),
            onRevertToServerResolved: () => this.removeBlockVariables(action),
            groupActions: () => {
                const deleteDefaultBlock = this.createDeleteBlockAction(
                    selectedBlock,
                    {
                        ...BlockTemplateDeleteBlockOptions,
                        shouldCreateRevertCommands: !isBaseBlockAContainerBlock
                    }
                );

                const replaceBlockWithTemplateAction =
                    this.replaceBlockWithClipboardAction(
                        copiedBlock,
                        selectedBlock,
                        copiedBlock.type,
                        allBlocks
                    );

                actionsManager.doNewApplyAction(deleteDefaultBlock);
                actionsManager.doNewApplyAction(replaceBlockWithTemplateAction);

                const createdBlock =
                    replaceBlockWithTemplateAction.ctx.commands.find(cmd => {
                        return (
                            cmd.type === CommandType.Create &&
                            (cmd?.data as TBlock)?.type === copiedBlock.type
                        );
                    }).data as TBlock;

                const isInvoiceBlock = copiedBlock.isInvoiceContainerBlock;
                const isLeadForm = selectedFlow.isLeadForm;

                if (isLeadForm && isInvoiceBlock) {
                    this.changeInvoiceForPublicFlow(selectedFlow);
                } else if (!isLeadForm && isInvoiceBlock) {
                    this.changePaymentsDueToday(selectedFlow);
                }

                const allCreatedBlocks =
                    createdBlock instanceof ContainerableBlock
                        ? [createdBlock, ...createdBlock.allDescendants()]
                        : [createdBlock];
                allCreatedBlocks.forEach(block => {
                    block.afterActionsApplied();
                    block.onBlockCreated();
                });

                const blockToSelect = createdBlock?.isContainerBlock()
                    ? createdBlock?.calculateRootLevelParent() || createdBlock
                    : createdBlock;

                selectAndScrollToBlock({
                    block: blockToSelect,
                    focusBlock: true
                });
            }
        }));

        toastStore.add({
            type: ToastType.CustomInfo,
            text: `Block pasted succesfully`,
            autoDismissMs: 5000,
            icon: CheckCircleIcon,
            iconColor: colors.neutral900,
            customColor: colors.blue20
        });

        // if in builder (flow instance) and pasted a contract
        if (!selectedFlow.is_template && copiedBlock.isContractBlock) {
            toastStore.add({
                type: ToastType.Info,
                text: `Smart fields and signature were updated to this project details`,
                autoDismissMs: 5000
            });
        }

        setDidPasteFromClipboard(true);
    }

    createBlankInvoice(
        invoiceType: TInvoiceType,
        invoiceBlock: InvoiceBlock,
        blockType: TBlockType
    ) {
        const { actionsManager } = this.rootStore;

        actionsManager.groupActions((action: FlowActionGroup) => ({
            name: ActionName.CreateBlankInvoice,
            serverActionType: ServerActionType.UpdateBlockToTemplate,
            applySideEffects: () => this.selectCreatedBlock(action, blockType),
            onApplyToServerResolved: (result, { variables: newVariables }) =>
                this.addBlockVariables(action, newVariables),
            onRevertToServerResolved: () => this.removeBlockVariables(action),
            groupActions: () => {
                this.updateBlock(invoiceBlock, {
                    invoice_type: invoiceType
                });

                if (invoiceType === 'recurring') {
                    this.updateBlock(invoiceBlock.chargeBlock, {
                        allow_gratuity: false,
                        gratuity_type: 'all_payments'
                    });
                }

                this.replaceBlockWithBlock(invoiceBlock, blockType);
            }
        }));
    }

    replaceBlockWithBlock<T extends TBlockType>(
        block: TBlock,
        blockType: T,
        initialAttributes: Partial<TBlock & { type: T }> = {},
        applySideEffects?: () => void
    ) {
        this.reuseBlockWithLimitationsCheck({
            targetBlock: block,
            sourceBlock: { type: blockType, ...initialAttributes } as TBlock,
            onReuse: limitationResolution => {
                const { actionsManager } = this.rootStore;

                actionsManager.groupActions((action: FlowActionGroup) => ({
                    name: ActionName.UpdateBlockToTemplate,
                    serverActionType: ServerActionType.UpdateBlockToTemplate,
                    applySideEffects: () => {
                        applySideEffects?.();
                        this.selectCreatedBlock(action, blockType);
                    },
                    onApplyToServerResolved: (
                        result,
                        { variables: newVariables }
                    ) => this.addBlockVariables(action, newVariables),
                    onRevertToServerResolved: () =>
                        this.removeBlockVariables(action),
                    groupActions: () => {
                        const deleteOldBlockAction =
                            this.createDeleteBlockAction(
                                block,
                                BlockTemplateDeleteBlockOptions
                            );

                        const newBlockAction = this.createCreateBlockAction({
                            block: block.parent,
                            blockType,
                            order: block.order,
                            initialAttributes:
                                blockType === 'invoice_container_block'
                                    ? {
                                          ...initialAttributes,
                                          invoice_type: (block as InvoiceBlock)
                                              .invoice_type
                                      }
                                    : initialAttributes,
                            compositionType: CompositionType.Default
                        });

                        actionsManager.doNewApplyAction(deleteOldBlockAction);
                        actionsManager.doNewApplyAction(newBlockAction);
                        newBlockAction.ctx.newTargetBlock.afterActionsApplied();
                        limitationResolution();
                    }
                }));
            }
        });
    }

    private selectCreatedBlock(action: FlowActionGroup, blockType: TBlockType) {
        const { commands } = action.ctx;

        const createdBlockId = (
            commands.find(cmd => {
                return (
                    cmd.type === CommandType.Create &&
                    (cmd?.data as TBlock)?.type === blockType
                );
            })?.data as TBlock
        )?.block_id;

        this.rootStore.builderUiStore.selectBlock(createdBlockId);
    }

    async replaceBlockWithTemplate(
        templateId: string,
        block: TBlock,
        blockType: TBlockType
    ) {
        const { flowCtrl, flowStore } = this.rootStore;

        const {
            flow: template,
            blocks,
            pages,
            variables
        } = await flowCtrl.fetchPlainFlow(templateId);
        const blockTemplate = flowStore.load(
            template,
            pages,
            blocks,
            variables
        );

        const templateBlock = blockTemplate.pages[0].rootBlock.childBlocks[0];
        this.replaceBlockWithBlockAction({
            fromBlock: block,
            toBlock: templateBlock,
            newBlockType: blockType,
            templateId
        });
    }

    async importSingularActionBlockFromFlow(
        fromBlock: TBlock,
        flowId: string,
        { blockId, blockType }: IFindBlockBy,
        track?: ReturnType<typeof useAnalytics>['track'],
        onImport?: () => void
    ) {
        const {
            builderUiStore: { selectedFlow },
            flowCtrl,
            flowStore,
            toastStore
        } = this.rootStore;

        await flowCtrl.fetchFlowToImport(flowId);
        const flow = flowStore.flows[flowId];

        const toBlock = flow.blocks.find(block => {
            if (blockId) {
                return block.block_id === blockId;
            }

            return block.type === blockType;
        });

        this.importBlock(fromBlock, toBlock, flow._id, () => {
            onImport?.();
            trackAndHandleErrorForUploadQuestion();
        });

        // This is temporary until we return upload_question_blocks_count when loading templates (ECO-2040)
        function trackAndHandleErrorForUploadQuestion() {
            const uploadQuestionLimitation = checkLimitations(
                {
                    container_block: [
                        forbidMoreThanMaxUploadQuestionBlocksForCompositionBlock
                    ]
                },
                {
                    sourceBlock: toBlock as BlockLike,
                    targetFlow: selectedFlow
                }
            );

            if (uploadQuestionLimitation.status === 'disabled') {
                toastStore.add({
                    type: ToastType.Error,
                    text: uploadQuestionLimitation.reason
                });
                track?.(AnalyticsEvents.ImportActionBlockFromFlow, {
                    action: 'error',
                    error_type: 'limitations',
                    reason: uploadQuestionLimitation.reason,
                    block_type: 'question_block',
                    question_type: QuestionType.Upload
                });
                throw new Error(
                    'Creation limitation, upload question block is disabled'
                );
            }
        }
    }

    importBlock(
        fromBlock: TBlock,
        toBlock: TBlock,
        templateId: string,
        onImport?: () => void
    ) {
        this.reuseBlockWithLimitationsCheck({
            sourceBlock: toBlock,
            targetBlock: fromBlock,
            onReuse: limitationResolution => {
                let _toBlock = toBlock;
                const isContentBlock = !actionBlockTypes[toBlock.type];
                if (isContentBlock && !toBlock.isRootChild) {
                    _toBlock = toBlock.parent.parent;
                }
                this.replaceBlockWithBlockAction({
                    fromBlock,
                    toBlock: _toBlock,
                    newBlockType: fromBlock.type,
                    templateId,
                    isImport: true,
                    fixRestrictedPageOrder: () => {
                        onImport?.();
                        limitationResolution();
                    }
                });
            }
        });
    }

    private reuseBlockWithLimitationsCheck({
        sourceBlock,
        targetBlock,
        onReuse
    }: {
        targetBlock: TBlock;
        sourceBlock: TBlock;
        // the resolution is passed back to the caller to keep this method pure
        // and also because i don't know if this method was called inside a group action or not.
        // if not, the caller will have to make sure that it and the resolution are properly grouped.
        onReuse: (resolution: () => void | null) => void;
    }) {
        const { pageCtrl, builderUiStore, toastStore } = this.rootStore;

        const limitation = checkLimitations(reuseBlockCreationLimitations, {
            targetBlock: targetBlock as BlockLike,
            sourceBlock: sourceBlock as BlockLike,
            targetFlow: targetBlock.flow,
            targetPage: targetBlock.page,
            sourceForMessage: 'reuse'
        });

        let resolution = () => {};
        const success = () => {
            builderUiStore.setCreationLimitDialogDetails(null);
            onReuse(resolution);
        };

        switch (limitation.status) {
            case 'allowed': {
                success();
                break;
            }
            case 'disabled': {
                toastStore.add({
                    type: ToastType.Error,
                    text: limitation.reason
                });
                break;
            }
            case 'restricted': {
                switch (limitation.issueType) {
                    case 'forbidContractWithRecapsBeforeServiceSelection':
                    case 'forbidServiceBlockAfterInvoiceBlock':
                    case 'forbidServiceBlockAfterContractBlockWithServiceRecaps':
                    case 'forbidSessionSelectionBlockAfterSchedulerBlock': {
                        const { solutionData } = limitation;
                        resolution = () => {
                            pageCtrl.movePageRelativeToPage({
                                pageIdToMove: solutionData.droppedItemId,
                                relativeToPageId: solutionData.dropTargetId,
                                direction: solutionData.direction
                            });
                        };
                        break;
                    }
                }

                const { reason } = limitation;
                builderUiStore.setCreationLimitDialogDetails({
                    title: reason.short,
                    description: reason.long,
                    approveCTA: {
                        text: reason.cta,
                        callback: success
                    },
                    rejectCTA: {
                        text: reason.rejectCta
                    }
                });
            }
        }
    }

    private replaceBlockWithBlockAction({
        fromBlock,
        toBlock,
        newBlockType,
        templateId,
        isImport,
        fixRestrictedPageOrder
    }: {
        fromBlock: TBlock;
        toBlock: TBlock;
        newBlockType: TBlockType;
        templateId: string;
        isImport?: boolean; // This is a temporary field until we wil get rid of the block template feature.
        fixRestrictedPageOrder?: () => void;
    }) {
        const {
            actionsManager,
            eventEmitterCtrl,
            builderUiStore: { selectedFlow }
        } = this.rootStore;

        actionsManager.groupActions((action: FlowActionGroup) => ({
            name: isImport
                ? ActionName.ImportBlock
                : ActionName.UpdateBlockToTemplate,
            serverActionType: ServerActionType.UpdateBlockToTemplate,
            applySideEffects: () => {
                const createdBlock = action.ctx.commands.find(
                    cmd =>
                        cmd.type === CommandType.Create &&
                        (cmd?.data as TBlock)?.type === newBlockType
                ).data as TBlock;
                const rootLevelParent =
                    createdBlock?.calculateRootLevelParent();

                const allCreatedBlocks =
                    createdBlock instanceof ContainerableBlock
                        ? [createdBlock, ...createdBlock.allDescendants()]
                        : [createdBlock];
                allCreatedBlocks.forEach(block => block.onBlockCreated());

                let blockToScrollTo = rootLevelParent || createdBlock;
                if (isImport && newBlockType === 'payment_schedule_block') {
                    blockToScrollTo = createdBlock;
                }

                this.rootStore.builderUiStore.selectAndScrollToBlock({
                    block: blockToScrollTo,
                    focusBlock: true
                });
            },
            onApplyToServerResolved: (result, { variables: newVariables }) => {
                this.addBlockVariables(action, newVariables);
                const createdBlock = action.ctx.commands.find(
                    cmd =>
                        cmd.type === CommandType.Create &&
                        (cmd?.data as TBlock)?.type === newBlockType
                ).data as TBlock;
                eventEmitterCtrl.trigger(CoreOutboundEvents.BlockSelected, {
                    blockId: createdBlock.block_id,
                    isPaymentScheduleUpdate:
                        newBlockType === 'payment_schedule_block'
                });
            },
            onRevertToServerResolved: () => this.removeBlockVariables(action),
            createApplyDataForServer: () =>
                this.getAttributedDataForServer(
                    action.actionAs<FlowAction<IAttributionContext>>()
                ),
            groupActions: () => {
                if (fixRestrictedPageOrder) {
                    fixRestrictedPageOrder();
                }

                const deleteDefaultBlock = this.createDeleteBlockAction(
                    fromBlock,
                    BlockTemplateDeleteBlockOptions
                );

                const replaceBlockWithTemplateAction =
                    this.replaceBlockWithTemplateAction(
                        toBlock,
                        fromBlock,
                        newBlockType,
                        templateId
                    );

                actionsManager.doNewApplyAction(deleteDefaultBlock);
                actionsManager.doNewApplyAction(replaceBlockWithTemplateAction);
                replaceBlockWithTemplateAction.ctx.duplicate_block?.afterActionsApplied();

                const isLeadFormInvoiceBlock =
                    selectedFlow.isLeadForm && toBlock.isInvoiceContainerBlock;

                if (isLeadFormInvoiceBlock) {
                    this.changeInvoiceForPublicFlow(selectedFlow);
                }
            }
        }));
    }

    updateBlockLocally(block, data) {
        block.flowRevision.setBlockData(block.block_id, data);
    }

    changeBlockQuestionType(block: QuestionBlock, config: IQuestionTypeConfig) {
        const questionBlock = block;
        const alteredQuestion = questionBlock.getAlteredQuestionDataByType(
            config.type,
            config
        );

        this.updateBlock(
            block,
            {
                question: alteredQuestion
            },
            { updateDataHandleMethod: DataHandleMethod.replace }
        );
    }

    setQuestionBlockTypeStage(block, config: IQuestionTypeConfig) {
        const { actionsManager } = this.rootStore;
        const questionBlock = block as QuestionBlock;

        const action =
            actionsManager.getActionByIdParam<ICreateQuestionBlockContext>({
                blockId: questionBlock.block_id,
                pageId: questionBlock.page_id,
                actionName: ActionName.CreateQuestionBlock
            });

        if (!action) {
            // In that case the question is already created and now we need to update it instead of going to the next stage.
            this.changeBlockQuestionType(block, config);

            return;
        }

        const alteredQuestion = questionBlock.getAlteredQuestionDataByType(
            config.type,
            config
        );

        action.setContext({
            blockId: questionBlock.block_id,
            data: {
                question: alteredQuestion
            }
        });

        actionsManager.applyStageForAction(
            action,
            CreateQuestionBlockStages.updateQuestionType
        );
    }

    // #endregion

    // #region action creators

    createCreateBlockAction({
        block,
        blockType,
        order,
        layout,
        initialAttributes = {},
        compositionType,
        options = {}
    }: ICreateCreateBlockActionProps) {
        const { builderUiStore, actionsManager } = this.rootStore;
        const { origin } = initialAttributes;
        const { applyResolvedCallback } = options;
        return actionsManager.createAction<ICreateBlockContext>(
            (newAction: Action<ICreateBlockContext>) => ({
                name: ActionName.CreateBlock,
                description: this.getBlockActionDescription({
                    actionType: 'create',
                    blockType,
                    origin
                }),
                serverActionType: ServerActionType.CreateBlock,
                ctx: {
                    flow: builderUiStore.selectedFlow,
                    pageId: block.page_id,
                    blockId: block.block_id,
                    blockType,
                    order,
                    compositionType
                },
                createCommands: () =>
                    this.createBlockCreateCommands(newAction, {
                        initialAttributes,
                        layout
                    }),
                applySideEffects: () => {
                    this.applyCreateBlockSideEffects({
                        action: newAction,
                        options
                    });
                },
                revertSideEffects: () =>
                    this.revertCreateBlockSideEffects(newAction),
                createApplyDataForServer: () =>
                    this.getBaseDataForServer(newAction),
                createRevertDataForServer: () =>
                    this.getRevertBaseDataForServer(newAction),
                onApplyToServerResolved: (result, { variables }) => {
                    newAction.setContext({ variables });
                    if (applyResolvedCallback) {
                        applyResolvedCallback();
                    }
                },
                onRevertToServerResolved: () =>
                    this.removeBlockVariables(newAction)
            })
        );
    }

    createCompositionCreateBlockAction({
        block,
        blockType,
        compositionContentType,
        compositionLayoutType,
        compositionType,
        layout = null,
        order,
        origin,
        initialAttributes = {}
    }: {
        block: TBlock;
        blockType: TBlockType;
        compositionContentType: CompositionContentType;
        compositionLayoutType?: CompositionLayoutType;
        compositionType: CompositionType;
        layout: ICompositionLayoutStruct;
        order: number;
        origin: string;
        initialAttributes: any;
    }) {
        const { builderUiStore, actionsManager } = this.rootStore;

        return actionsManager.createMultiStageAction<ICreateCompositionBlockContext>(
            (
                newAction: MultiStageFlowAction<ICreateCompositionBlockContext>
            ) => ({
                name: ActionName.CreateCompositionBlock,
                serverActionType: ServerActionType.CreateBlock,
                description: this.getBlockActionDescription({
                    actionType: 'create',
                    blockType,
                    compositionType,
                    compositionContentType,
                    origin
                }),
                ctx: {
                    flow: builderUiStore.selectedFlow,
                    pageId: block.page_id,
                    blockId: block.block_id,
                    blockType,
                    compositionType,
                    order,
                    compositionContentType
                },
                stages: {
                    [CreateCompositionBlockStages.createDefaultBlock]: {
                        createCommands: () =>
                            this.createBlockCreateCommands(newAction, {
                                layout,
                                initialAttributes: {
                                    compositionContentType,
                                    compositionLayoutType,
                                    ...initialAttributes
                                }
                            }),
                        applySideEffects: () =>
                            this.applyCreateBlockSideEffects({
                                action: newAction,
                                options: {
                                    compositionLayoutType
                                }
                            }),
                        revertSideEffects: () =>
                            this.revertCreateBlockSideEffects(newAction),
                        order: 0
                    },
                    [CreateCompositionBlockStages.deleteCurrentLayout]: {
                        createCommands: () =>
                            this.createDeleteBlockCommands(newAction, {
                                shouldCreateRevertCommands: false
                            }),
                        order: 1
                    },
                    [CreateCompositionBlockStages.createCompositionLayout]: {
                        createCommands: () =>
                            this.createBlockCreateCommands(newAction, {
                                layout: newAction.ctx.layout
                            }),
                        applySideEffects: () =>
                            this.applyCreateBlockSideEffects({
                                action: newAction
                            }),
                        revertSideEffects: () =>
                            this.revertCreateBlockSideEffects(newAction),
                        order: 2
                    }
                },
                createApplyDataForServer: () =>
                    this.getBaseDataForServer(newAction),
                createRevertDataForServer: () =>
                    this.getRevertBaseDataForServer(newAction),
                onApplyToServerResolved: (result, { variables }) =>
                    this.addBlockVariables(newAction, variables),
                onRevertToServerResolved: () =>
                    this.removeBlockVariables(newAction)
            })
        );
    }

    createQuestionCreateBlockAction(
        block: TBlock,
        blockType: TBlockType,
        order: number,
        options: ICreateBlockOptions = {},
        initialAttributes: PartialDeep<QuestionBlock> = {}
    ) {
        const { builderUiStore, actionsManager } = this.rootStore;

        return actionsManager.createMultiStageAction<ICreateQuestionBlockContext>(
            (newAction: MultiStageFlowAction<ICreateQuestionBlockContext>) => ({
                name: ActionName.CreateQuestionBlock,
                serverActionType: ServerActionType.CreateBlock,
                description: this.getBlockActionDescription({
                    actionType: 'create',
                    blockType
                }),
                ctx: {
                    flow: builderUiStore.selectedFlow,
                    pageId: block.page_id,
                    blockId: block.block_id,
                    updateDataHandleMethod: DataHandleMethod.replace,
                    blockType,
                    order
                },
                stages: {
                    [CreateQuestionBlockStages.createDefaultBlock]: {
                        createCommands: () => {
                            this.createBlockCreateCommands(newAction, {
                                initialAttributes
                            });
                            newAction.setItemId(
                                newAction.ctx.newTargetBlock.block_id
                            );
                        },
                        applySideEffects: () =>
                            this.applyCreateBlockSideEffects({
                                action: newAction,
                                options
                            }),
                        revertSideEffects: () =>
                            this.revertCreateBlockSideEffects(newAction),
                        order: 0
                    },
                    [CreateQuestionBlockStages.updateQuestionType]: {
                        createCommands: () =>
                            this.createBlockUpdateCommands(newAction),
                        stageCommandsRelation: CommandsRelation.accumulate,
                        order: 1
                    }
                },
                createApplyDataForServer: () =>
                    this.getBaseDataForServer(newAction),
                createRevertDataForServer: () =>
                    this.getRevertBaseDataForServer(newAction),
                onApplyToServerResolved: (result, { variables }) =>
                    this.addBlockVariables(newAction, variables),
                onRevertToServerResolved: () =>
                    this.removeBlockVariables(newAction)
            })
        );
    }

    addBlockVariables(action, variables) {
        const { builderUiStore } = this.rootStore;

        builderUiStore.selectedFlow.addVariables(variables);
        builderUiStore.selectedFlow.updateBlocksWithConnectedVariableIds(
            variables
        );

        action.setContext({ variables });
    }

    removeBlockVariables(action) {
        const { builderUiStore } = this.rootStore;
        const { variables } = action.ctx;

        if (!variables) {
            return;
        }

        builderUiStore.selectedFlow.removeVariables(variables);
        builderUiStore.selectedFlow.updateBlocksWithDisconnectedVariableIds(
            variables
        );
    }

    createUpdateBlockAction<Block extends TBlock>(
        block: Block,
        data: PartialDeep<Block>,
        {
            oldData,
            updateDataHandleMethod = UpdateBlockOptionsDefaults.updateDataHandleMethod,
            applyResolvedCallback = () => {},
            applySideEffectsCallback = () => {},
            revertSideEffectsCallback = () => {}
        }: IUpdateBlockOptions = UpdateBlockOptionsDefaults
    ) {
        const {
            builderUiStore: { selectedFlow, setIsContractAddedOrChanged },
            actionsManager,
            toastStore
        } = this.rootStore;

        this.ensureNotInMultiStageCreate(block);

        return actionsManager.createAction<IUpdateBlockContext>(
            (newAction: Action<IUpdateBlockContext>) => ({
                name: ActionName.UpdateBlockData,
                serverActionType: ServerActionType.UpdateBlock,
                description: this.getBlockActionDescription({
                    actionType: 'update',
                    blockType: block.type
                }),
                ctx: {
                    flow: selectedFlow,
                    pageId: block.page_id,
                    blockId: block.block_id,
                    data,
                    oldData,
                    updateDataHandleMethod
                },
                createCommands: () => this.createBlockUpdateCommands(newAction),
                applySideEffects: () => {
                    applySideEffectsCallback();
                    if (
                        block.isContractHtmlBlock ||
                        block.isContractBlock ||
                        block.isSignatureBlock
                    ) {
                        setIsContractAddedOrChanged(true);
                    }
                },
                revertSideEffects: () => {
                    revertSideEffectsCallback();
                },
                onApplyToServerResolved: () => {
                    applyResolvedCallback();
                },
                onApplyToServerFailed: actionError => {
                    if (
                        actionError.actionResult?.action_error_type ===
                        FlowApplyActionsErrorsTypes.cardOnFileError
                    ) {
                        toastStore.add({
                            type: ToastType.Error,
                            text: actionError.actionResult
                                ?.action_validation_error_message
                        });
                    }
                },
                createApplyDataForServer: () =>
                    this.getBaseDataForServer(newAction),
                createRevertDataForServer: () =>
                    this.getRevertBaseDataForServer(newAction)
            })
        );
    }

    createUpdateBlockColumnsAction(block: ContainerBlock, nColumns: number) {
        const { builderUiStore, actionsManager } = this.rootStore;

        return actionsManager.createAction<IUpdateBlockColumnsContext>(
            (newAction: Action<IUpdateBlockColumnsContext>) => ({
                name: ActionName.UpdateBlockColumns,
                serverActionType: ServerActionType.UpdateBlockColumns,
                ctx: {
                    flow: builderUiStore.selectedFlow,
                    pageId: block.page_id,
                    blockId: block.block_id,
                    nColumns
                },
                createCommands: () =>
                    this.createUpdateBlockColumnsCommands(newAction),
                applySideEffects: () =>
                    this.setChildrenParents(
                        newAction.ctx.flow,
                        newAction.ctx.movedChildrenNewParents
                    ),
                revertSideEffects: () =>
                    this.setChildrenParents(
                        newAction.ctx.flow,
                        newAction.ctx.movedChildrenOldParents
                    ),
                createApplyDataForServer: () =>
                    this.getBaseDataForServer(newAction),
                createRevertDataForServer: () =>
                    this.getRevertBaseDataForServer(newAction)
            })
        );
    }

    createUpdateServiceSelectionLayoutAction(
        block: ServiceSelectionBlock,
        newLayout: ServicesItemsLayoutType
    ) {
        const { builderUiStore, actionsManager } = this.rootStore;

        return actionsManager.createAction<IUpdateServiceSelectionLayoutContext>(
            (newAction: Action<IUpdateServiceSelectionLayoutContext>) => ({
                name: ActionName.UpdateBlockColumns,
                serverActionType: ServerActionType.UpdateBlockColumns,
                ctx: {
                    flow: builderUiStore.selectedFlow,
                    pageId: block.page_id,
                    blockId: block.block_id,
                    newLayout
                },
                createCommands: () =>
                    this.createUpdateServiceSelectionLayoutCommands(newAction),
                applySideEffects: () =>
                    this.setChildrenParents(
                        newAction.ctx.flow,
                        newAction.ctx.movedChildrenNewParents
                    ),
                revertSideEffects: () =>
                    this.setChildrenParents(
                        newAction.ctx.flow,
                        newAction.ctx.movedChildrenOldParents
                    ),
                createApplyDataForServer: () =>
                    this.getBaseDataForServer(newAction),
                createRevertDataForServer: () =>
                    this.getRevertBaseDataForServer(newAction)
            })
        );
    }

    createUpdateMultipleBlocksAction(
        block: ContainerBlock,
        oldData: Array<Partial<TBlock>>,
        data: Array<Partial<TBlock>>
    ) {
        const { builderUiStore, actionsManager } = this.rootStore;

        this.ensureNotInMultiStageCreate(block);
        this.ensureNotInMultiStageCreate(block.parent);

        return actionsManager.createAction<IUpdateBlockContext>(
            (newAction: Action<IBulkUpdateBlockContext>) => ({
                name: ActionName.UpdateMultipleBlocks,
                serverActionType: ServerActionType.UpdateBlock,
                ctx: {
                    flow: builderUiStore.selectedFlow,
                    pageId: block.page_id,
                    blockId: block.block_id,
                    oldData,
                    data
                },
                createCommands: () =>
                    this.createBulkBlocksUpdateCommands(newAction),
                createApplyDataForServer: () =>
                    this.getBulkUpdateBlockDataForServer(newAction),
                createRevertDataForServer: () =>
                    this.getBulkRevertBlockDataForServer(newAction)
            })
        );
    }

    createDeleteBlockAction(
        block: TBlock,
        options: IDeleteBlockOptions = DefaultDeleteBlockOptions
    ) {
        const { builderUiStore, actionsManager } = this.rootStore;

        return actionsManager.createAction<IBlockBaseContext>(
            (newAction: Action<IBlockBaseContext>) => ({
                name: ActionName.DeleteBlock,
                description: this.getBlockActionDescription({
                    actionType: 'delete',
                    blockType: block.type
                }),
                serverActionType: ServerActionType.DeleteBlock,
                ctx: {
                    flow: builderUiStore.selectedFlow,
                    pageId: block.page_id,
                    blockId: block.block_id
                },
                createCommands: () =>
                    this.createDeleteBlockCommands(newAction, options),
                createApplyDataForServer: () =>
                    this.getBaseDataForServer(newAction),
                onApplyToServerResolved: () => {
                    options?.applyResolvedCallback();
                },
                createRevertDataForServer: () =>
                    this.getRevertBaseDataForServer(newAction)
            })
        );
    }

    createMoveBlockAction(
        block: TBlock,
        parent: ContainerableBlock,
        order: number
    ) {
        const { builderUiStore, actionsManager } = this.rootStore;

        return actionsManager.createAction<IMoveBlockContext>(
            (newAction: Action<IMoveBlockContext>) => {
                return {
                    name: ActionName.MoveBlock,
                    description: this.getBlockActionDescription({
                        actionType: 'move',
                        blockType: block.type
                    }),
                    serverActionType: ServerActionType.UpdateBlock,
                    ctx: {
                        flow: builderUiStore.selectedFlow,
                        pageId: block.page_id,
                        blockId: block.block_id,
                        movedChildrenNewParents: {
                            [block.block_id]: parent.block_id
                        },
                        movedChildrenOldParents: {
                            [block.block_id]: block.parent.block_id
                        },
                        order,
                        oldOrder: block.order
                    },
                    createCommands: () =>
                        this.createMoveBlocksCommands(newAction),
                    applySideEffects: () =>
                        this.setChildrenParents(
                            newAction.ctx.flow,
                            newAction.ctx.movedChildrenNewParents
                        ),
                    revertSideEffects: () =>
                        this.setChildrenParents(
                            newAction.ctx.flow,
                            newAction.ctx.movedChildrenOldParents
                        ),
                    createApplyDataForServer: () =>
                        this.getBaseDataForServer(newAction),
                    createRevertDataForServer: () =>
                        this.getRevertBaseDataForServer(newAction)
                };
            }
        );
    }

    createDuplicateBlockAction(block: TBlock) {
        const { builderUiStore, actionsManager } = this.rootStore;

        return actionsManager.createAction<IAttributedDupliacteBlockCtx>(
            (newAction: Action<IAttributedDupliacteBlockCtx>) => ({
                name: ActionName.DuplicateBlock,
                description: this.getBlockActionDescription({
                    actionType: 'duplicate',
                    blockType: block.type
                }),
                serverActionType: ServerActionType.CreateBlock,
                ctx: {
                    flow: builderUiStore.selectedFlow,
                    pageId: block.page_id,
                    blockId: block.block_id,
                    source_block_id: block.block_id,
                    source_flow_id: builderUiStore.selectedFlowId
                },
                createCommands: () =>
                    this.createBlockDuplicateCommands(newAction),
                createApplyDataForServer: () =>
                    this.getAttributedDataForServer(newAction),
                createRevertDataForServer: () =>
                    this.getRevertBaseDataForServer(newAction)
            })
        );
    }

    createPackageItemsBlockAction({
        block,
        item,
        scope,
        serviceImage,
        services = []
    }: {
        block: TBlock;
        item: TESServiceOrPackage;
        scope: ServiceItemScope;
        serviceImage: IImage;
        services: IServiceResponse[];
    }) {
        const { builderUiStore, actionsManager } = this.rootStore;

        return actionsManager.createAction<ICreatePackageBlockContext>(
            (newAction: Action<ICreatePackageBlockContext>) => ({
                name: ActionName.CreatePackagesItemsBlock,
                description: this.getBlockActionDescription({
                    actionType: 'create',
                    blockType: block.type
                }),
                serverActionType: ServerActionType.CreateBlock,
                ctx: {
                    flow: builderUiStore.selectedFlow,
                    pageId: block.page_id,
                    blockId: block.block_id,
                    block,
                    services,
                    item,
                    scope,
                    serviceImage
                },
                createCommands: () =>
                    this.createPackagesBlockCreateCommand(newAction),
                createApplyDataForServer: () =>
                    this.getBaseDataForServer(newAction),
                createRevertDataForServer: () =>
                    this.getRevertBaseDataForServer(newAction)
            })
        );
    }

    createPackageItemsBlockFromTemplateAction({
        block,
        item,
        scope,
        serviceImage,
        withTaxes = false
    }: {
        block: TBlock;
        item: IServiceTemplate;
        scope: ServiceItemScope;
        serviceImage: IImage;
        withTaxes?: boolean;
    }) {
        const { builderUiStore, actionsManager } = this.rootStore;

        return actionsManager.createAction<ICreatePackageBlockFromTemplateContext>(
            (newAction: Action<ICreatePackageBlockFromTemplateContext>) => ({
                name: ActionName.CreatePackagesItemsBlock,
                description: this.getBlockActionDescription({
                    actionType: 'create',
                    blockType: block.type
                }),
                serverActionType: ServerActionType.CreateBlock,
                ctx: {
                    flow: builderUiStore.selectedFlow,
                    pageId: block.page_id,
                    blockId: block.block_id,
                    block,
                    item,
                    scope,
                    serviceImage
                },
                createCommands: () =>
                    this.createPackagesBlockFromTemplateCreateCommand(
                        newAction,
                        withTaxes
                    ),
                createApplyDataForServer: () =>
                    this.getBaseDataForServer(newAction),
                createRevertDataForServer: () =>
                    this.getRevertBaseDataForServer(newAction)
            })
        );
    }

    replaceBlockWithClipboardAction(
        clipboardBlock: TBlock,
        block: TBlock,
        blockType: TBlockType,
        allRelevantBlocks: TBlock[]
    ) {
        const { builderUiStore, actionsManager } = this.rootStore;

        return actionsManager.createAction<IReplaceBlockFromClipContext>(
            (newAction: FlowAction<IReplaceBlockFromClipContext>) => ({
                name: ActionName.PasteBlock,
                description: this.getBlockActionDescription({
                    actionType: 'create',
                    blockType,
                    isFromPaste: true
                }),
                serverActionType: ServerActionType.UpdateBlockToTemplate,
                ctx: {
                    flow: builderUiStore.selectedFlow,
                    clipboardBlock,
                    source_block_id: clipboardBlock.block_id,
                    source_flow_id: clipboardBlock.flow_id,
                    pageId: block.page_id,
                    blockId: block.block_id,
                    blockType,
                    parent: block.parent,
                    order: block.order,
                    allRelevantBlocks,
                    originalBlockToReplace: block
                },
                createCommands: () =>
                    this.replaceBlockWithClipBoardCommands(newAction),
                applySideEffects: () =>
                    this.applyCreateBlockSideEffects({ action: newAction }),
                revertSideEffects: () =>
                    this.revertCreateBlockSideEffects(newAction),
                createApplyDataForServer: () =>
                    this.getAttributedDataForServer(newAction),
                createRevertDataForServer: () =>
                    this.getRevertBaseDataForServer(newAction),
                onApplyToServerResolved: (result, { variables }) =>
                    this.addBlockVariables(newAction, variables),
                onRevertToServerResolved: () =>
                    this.removeBlockVariables(newAction)
            })
        );
    }

    replaceBlockWithTemplateAction(
        templateBlock: TBlock,
        block: TBlock,
        blockType: TBlockType,
        templateId: string
    ) {
        const { builderUiStore, actionsManager } = this.rootStore;

        return actionsManager.createAction<IReplaceBlockContext>(
            (newAction: FlowAction<IReplaceBlockContext>) => ({
                name: ActionName.UpdateBlockToTemplate,
                serverActionType: ServerActionType.UpdateBlockToTemplate,
                ctx: {
                    flow: builderUiStore.selectedFlow,
                    templateBlock,
                    source_block_id: templateBlock.block_id,
                    pageId: block.page_id,
                    blockId: block.block_id,
                    blockType,
                    parent: block.parent,
                    order: block.order,
                    templateId,
                    source_flow_id: templateId
                },
                createCommands: () =>
                    this.replaceBlockWithTemplateCommands(newAction),
                applySideEffects: () =>
                    this.applyCreateBlockSideEffects({ action: newAction }),
                revertSideEffects: () =>
                    this.revertCreateBlockSideEffects(newAction),
                createApplyDataForServer: () =>
                    this.getAttributedDataForServer(newAction),
                createRevertDataForServer: () =>
                    this.getRevertBaseDataForServer(newAction),
                onApplyToServerResolved: (result, { variables }) =>
                    this.addBlockVariables(newAction, variables),
                onRevertToServerResolved: () =>
                    this.removeBlockVariables(newAction)
            })
        );
    }

    // #endregion

    // #region actions apply

    createBlockCreateCommands(
        action: Action<ICreateBlockContext>,
        {
            initialAttributes = null,
            layout = null
        }: ICreateBlockCommandsOptions = {}
    ) {
        const { builderUiStore } = this.rootStore;
        const { ctx } = action;

        const parentBlock = ctx.flow.draftRevision.blocksMap[ctx.blockId];
        if (!(parentBlock instanceof ContainerableBlock)) {
            throw new Error(
                `The parent block must be a container block. The given block ${parentBlock.block_id} type is ${parentBlock.constructor.name}`
            );
        }

        const {
            parentOldChildrenLayout,
            newBlocksMap,
            newTargetBlock,
            isCompositionLayout,
            commands
        } = this.createApplyCreateBlocksCommands(
            parentBlock,
            ctx.blockType,
            ctx,
            layout,
            initialAttributes
        );

        const revertCommands = this.createRevertCreateBlockCommands(
            newBlocksMap,
            parentBlock,
            parentOldChildrenLayout
        );

        action.setContext({
            newTargetBlock,
            isCompositionLayout,
            commands,
            revertCommands,
            oldSelectedBlockId: builderUiStore.selectedBlockId
        });
    }

    createRevertCreateBlockCommands(
        newBlocksMap: Record<string, TBlock>,
        parentBlock: TBlock,
        parentOldChildrenLayout: IChildLayout[]
    ) {
        const revertCommands: TCommand[] = Object.keys(newBlocksMap).map(x =>
            createDeleteBlockCommand(x)
        );

        revertCommands.push(
            createUpdateBlockCommand(parentBlock.block_id, {
                children: parentOldChildrenLayout
            })
        );

        return revertCommands;
    }

    // TODO: refactor layout and initialAttributes props to be optional
    createApplyCreateBlocksCommands(
        parentBlock: TBlock,
        blockType: TBlockType,
        ctx: ICreateBlockContext,
        layout: ICompositionLayoutStruct,
        initialAttributes: any
    ) {
        const {
            builderUiStore: { selectedFlow }
        } = this.rootStore;
        const parentOldChildrenLayout = [
            ...(parentBlock as ContainerableBlock).sortedChildLayouts
        ];

        const isCompositionLayout: boolean =
            blockType === 'container_block' ||
            blockType === 'signatures_block' ||
            blockType === 'company_info_block';

        const selectedLayout =
            layout ||
            getDefaultCompositionLayout(
                this.rootStore,
                ctx.compositionType,
                initialAttributes?.compositionContentType,
                initialAttributes?.compositionLayoutType,
                selectedFlow
            );

        const { newBlocksMap, newTargetBlock, newChildLayout } =
            isCompositionLayout
                ? createCompositionLayoutBlock(
                      parentBlock as ContainerBlock,
                      ctx.compositionType,
                      ctx.order,
                      this.rootStore,
                      selectedLayout,
                      initialAttributes
                  )
                : createBlock(
                      parentBlock as ContainerBlock,
                      blockType,
                      ctx.order,
                      this.rootStore,
                      initialAttributes
                  );

        const commands: TCommand[] = Object.values(newBlocksMap).map(newBlock =>
            createCreateBlockCommand(newBlock)
        );
        commands.push(
            createUpdateBlockCommand(parentBlock.block_id, {
                children: [...parentOldChildrenLayout, newChildLayout]
            })
        );

        return {
            parentOldChildrenLayout,
            newBlocksMap,
            isCompositionLayout,
            newTargetBlock,
            commands
        };
    }

    createPaymentBlock({
        paymentScheduleBlock,
        applyResolvedCallback
    }: {
        paymentScheduleBlock: PaymentScheduleBlock;
        applyResolvedCallback?: () => void;
    }) {
        if (paymentScheduleBlock.lastPayment) {
            const lastPaymentContent =
                paymentScheduleBlock.lastPayment.payment_content;

            const lastPaymentDateCalcType = lastPaymentContent.date_calc_type;

            const dateCalcType =
                lastPaymentDateCalcType === DateCalcType.Today
                    ? DateCalcType.DateSent
                    : lastPaymentDateCalcType;

            this.createBlock({
                block: paymentScheduleBlock,
                isParent: true,
                blockType: 'payment_block',
                direction: 'bottom',
                initialAttributes: {
                    payment_content: {
                        amount_calc_type: AmountCalcType.DivideEqually,
                        date_calc_type: dateCalcType,
                        fixed_date: lastPaymentContent.fixed_date,
                        smart_date_period: lastPaymentContent.smart_date_period,
                        smart_date_period_relation:
                            lastPaymentContent.smart_date_period_relation,
                        smart_date_period_type:
                            lastPaymentContent.smart_date_period_type
                    }
                },
                options: {
                    shouldSelectBlock: false,
                    applyResolvedCallback
                }
            });
        } else {
            this.createBlock({
                block: paymentScheduleBlock,
                blockType: 'payment_block',
                direction: 'bottom',
                isParent: true,
                options: {
                    applyResolvedCallback
                }
            });
        }
    }

    applyCreateBlockSideEffects({
        action,
        options = {}
    }: {
        action: Action<ICreateBlockContext>;
        options?: Omit<ICreateBlockOptions, 'invoiceToast'>;
    }) {
        const {
            flowCtrl,
            builderUiStore,
            eventEmitterCtrl,
            builderUiStore: { selectedFlow, setIsContractAddedOrChanged },
            userStore: { currUser }
        } = this.rootStore;
        const {
            ctx: {
                newTargetBlock,
                isCompositionLayout,
                compositionType,
                compositionContentType,
                blockId
            }
        } = action;
        const isMultiStageAction = action instanceof MultiStageFlowAction;
        const {
            shouldSelectBlock = true,
            sideEffectCallback,
            compositionLayoutType
        } = options;

        sideEffectCallback?.({
            createdBlockId: newTargetBlock.block_id,
            block: selectedFlow?.flowRevision.getBlock(blockId)
        });
        if (!shouldSelectBlock) {
            return;
        }

        flowCtrl.selectBlock({
            blockId: newTargetBlock?.block_id,
            isMultiStageAction
        });

        if (isCompositionLayout) {
            let contentCompositionSidebarLayout;
            const { sidebarLayout } =
                COMPOSITION_CONFIG_BY_TYPE[compositionType];
            if (typeof sidebarLayout === 'function') {
                contentCompositionSidebarLayout = sidebarLayout(
                    compositionContentType
                );
            }

            let selectedSidebarLayout =
                contentCompositionSidebarLayout || sidebarLayout;

            if (
                compositionContentType === CompositionContentType.CanvaImage &&
                !currUser?.has_canva_integration
            ) {
                selectedSidebarLayout = ImageSidebarScreen.SelectImage;
            }

            builderUiStore.setSelectedSidebarScreen(selectedSidebarLayout);

            if (compositionLayoutType) {
                builderUiStore.setInitialSidebarLayoutType(
                    compositionLayoutType
                );
            }
        }

        // We don't have access to isInAppBrowser, but we do have access to it
        // in eventEmmiterCtrl, so it's a bit hackey but i'm using it
        if (eventEmitterCtrl.isMobile && newTargetBlock) {
            scrollToBlock({
                blockId: newTargetBlock.block_id,
                isMobileApp: true
            });
        }

        if (newTargetBlock?.isContractBlock) {
            setIsContractAddedOrChanged(true);
        }
    }

    private revertCreateBlockSideEffects(action: Action<ICreateBlockContext>) {
        this.rootStore.builderUiStore.selectBlock(
            action.ctx.oldSelectedBlockId
        );
    }

    private createBlockUpdateCommands(action: Action<IUpdateBlockContext>) {
        const { ctx } = action;
        const block = ctx.flow.draftRevision.blocksMap[ctx.blockId];

        const { data, updateDataHandleMethod: dataHandleMethod } = ctx;
        const options = {
            dataHandleMethod
        };

        let { oldData } = ctx;
        if (oldData === undefined) {
            oldData = extractOldData(data, block);
        }

        action.setContext({
            commands: [createUpdateBlockCommand(block.block_id, data, options)],
            revertCommands: [
                createUpdateBlockCommand(block.block_id, oldData, options)
            ]
        });
    }

    private createBulkBlocksUpdateCommands(
        action: Action<IBulkUpdateBlockContext>
    ) {
        const { oldData, data } = action.ctx;

        const { commands, revertCommands } = bulkBlocksUpdate(oldData, data);

        action.setContext({
            commands,
            revertCommands
        });
    }

    private createUpdateBlockColumnsCommands(
        action: Action<IUpdateBlockColumnsContext>
    ) {
        const { flow, blockId, nColumns } = action.ctx;
        const block = <ContainerBlock>flow.draftRevision.getBlock(blockId);

        const {
            commands,
            revertCommands,
            movedChildrenNewParents,
            movedChildrenOldParents,
            createdVerticalContainers
        } = updateBlockColumnNumber(block, nColumns, this.rootStore);

        action.setContext({
            commands,
            createdVerticalContainers,
            revertCommands,
            movedChildrenNewParents,
            movedChildrenOldParents
        });
    }

    private createUpdateServiceSelectionLayoutCommands(
        action: Action<IUpdateServiceSelectionLayoutContext>
    ) {
        const { flow, blockId, newLayout } = action.ctx;
        const block = <ServiceSelectionBlock>(
            flow.draftRevision.getBlock(blockId)
        );

        const {
            commands,
            revertCommands,
            movedChildrenNewParents,
            movedChildrenOldParents
        } = updateServiceSelectionLayout(block, newLayout, this.rootStore);

        action.setContext({
            commands,
            revertCommands,
            movedChildrenNewParents,
            movedChildrenOldParents
        });
    }

    private setChildrenParents(
        flow,
        childIdToParentId: Record<string, string>
    ) {
        if (!childIdToParentId) {
            return;
        }

        const childrenIds = Object.keys(childIdToParentId);
        if (!childrenIds.length) {
            return;
        }

        childrenIds.forEach(block_id => {
            flow.draftRevision.blocksMap[block_id].parent_id =
                childIdToParentId[block_id];
        });
    }

    private createDeleteBlockCommands(
        action: Action<IBlockBaseContext>,
        options: IDeleteBlockOptions = DefaultDeleteBlockOptions
    ) {
        const { flow, blockId } = action.ctx;
        let block = flow.draftRevision.getBlock(blockId);

        // In case of a last service container's item, delete service container
        // This means the item is a service without any items (and it's parent is the dummy container)
        if (
            block.isServiceSelectionItemBlock &&
            block.parent.isServiceItemsContainerBlock &&
            block.parent.childBlocks.length === 1
        ) {
            block = block.parent;
        }

        const commands = deleteBlock(block, this.rootStore, options);

        if (!options.calledFromReplaceTemplate) {
            mergeArrayFields(
                commands,
                this.getDeletePageOrBlockSideEffectCommands(block, options)
            );
        }

        action.setContext(commands);
    }

    deleteAndUpdateParentBlock(block: TBlock, updateData: Record<string, any>) {
        const { actionsManager } = this.rootStore;

        actionsManager.groupActions(() => ({
            name: ActionName.DeleteBlock,
            serverActionType: ServerActionType.DeleteBlock,
            onApplyToServerResolved: () => null,
            applySideEffects: () =>
                this.rootStore.builderUiStore.selectBlock(
                    block.parent.block_id
                ),
            onRevertToServerResolved: () =>
                this.rootStore.builderUiStore.selectBlock(block.block_id),
            groupActions: () => {
                // Delete Block
                const deleteBlockAction = this.createDeleteBlockAction(block);
                actionsManager.doNewApplyAction(deleteBlockAction);

                // Update Parent Block
                const updateParentBlockAction = this.createUpdateBlockAction(
                    block.parent,
                    updateData
                );
                actionsManager.doNewApplyAction(updateParentBlockAction);
            }
        }));
    }

    deleteBlockAndUpdateVariable(
        blockToDelete: TBlock,
        blockToUpdate: TBlock,
        updateData: Record<string, any>
    ) {
        const { actionsManager } = this.rootStore;

        actionsManager.groupActions(() => ({
            name: ActionName.DeleteBlock,
            serverActionType: ServerActionType.DeleteBlock,
            onApplyToServerResolved: () => null,
            applySideEffects: () => {
                this.rootStore.builderUiStore.selectBlock(
                    blockToUpdate.block_id
                );
            },
            onRevertToServerResolved: () =>
                this.rootStore.builderUiStore.selectBlock(
                    blockToDelete.block_id
                ),
            groupActions: () => {
                // Delete Block
                const deleteBlockAction =
                    this.createDeleteBlockAction(blockToDelete);
                actionsManager.doNewApplyAction(deleteBlockAction);

                // Update Parent Block`
                const updateBlockVariableAction =
                    this.rootStore.variableCtrl.createUpdateVariableAction(
                        blockToUpdate.connectedVariable,
                        updateData
                    );
                actionsManager.doNewApplyAction(updateBlockVariableAction);
            }
        }));
    }

    updateBlockAndUpdateVariable(
        blockToUpdate: TBlock,
        updateBlockData: Record<string, any>,
        updateVariableData: Record<string, any>,
        blockToUpdateVariable?: TBlock // optional- if not same block
    ) {
        const { actionsManager, builderUiStore, variableCtrl } = this.rootStore;
        const blockOfVariableToUpdate = blockToUpdateVariable || blockToUpdate;
        actionsManager.groupActions(() => ({
            name: ActionName.UpdateBlockData,
            serverActionType: ServerActionType.UpdateBlock,
            onRevertToServerResolved: () => {
                builderUiStore.selectBlock(blockToUpdate.block_id);
            },
            groupActions: () => {
                // Update Block Data
                const updateBlockAction = this.createUpdateBlockAction(
                    blockToUpdate,
                    updateBlockData
                );
                actionsManager.doNewApplyAction(updateBlockAction);

                // Update Block variable
                const updateBlockVariableAction =
                    variableCtrl.createUpdateVariableAction(
                        blockOfVariableToUpdate.connectedVariable,
                        updateVariableData
                    );
                actionsManager.doNewApplyAction(updateBlockVariableAction);
            }
        }));
    }

    createChildAndUpdateBlock(
        block: TBlock,
        updateData: Record<string, any>,
        options: {
            blockType: TBlockType;
            direction: TBlockCreationDirection;
            isParent?: boolean;
            sideEffectCallback: CreateBlockSideEffectFunction;
            shouldSelectBlock: boolean;
            initialAttributes?: Partial<TBlock>;
        }
    ) {
        const { actionsManager, eventEmitterCtrl } = this.rootStore;

        let newCreatedBlockId = null;
        actionsManager.groupActions(() => ({
            name: ActionName.CreateBlock,
            serverActionType: ServerActionType.CreateBlock,
            applySideEffects: () => {
                if (!eventEmitterCtrl.isMobile) {
                    this.rootStore.builderUiStore.selectBlock(
                        newCreatedBlockId
                    );
                }
            },
            onRevertToServerResolved: () =>
                this.rootStore.builderUiStore.selectBlock(block.block_id),
            groupActions: () => {
                // Update Block
                const updateBlockAction = this.createUpdateBlockAction(
                    block,
                    updateData
                );
                actionsManager.doNewApplyAction(updateBlockAction);

                // Create Child Block
                const order = getNewBlockOrder(
                    block,
                    options.direction,
                    options.isParent
                );

                const createBlockAction = this.createCreateBlockAction({
                    block,
                    blockType: options.blockType,
                    order,
                    options: {
                        sideEffectCallback: options?.sideEffectCallback,
                        shouldSelectBlock: options?.shouldSelectBlock
                    },
                    initialAttributes: options.initialAttributes
                });

                actionsManager.doNewApplyAction(createBlockAction);

                newCreatedBlockId =
                    createBlockAction.ctx.newTargetBlock.block_id;
            }
        }));
    }

    isHtmlBlockContentMatch(
        blockId: string,
        editorContent: string,
        htmlString: string
    ) {
        return (
            translateHTMLEntitiesForComparison(htmlString || '') ===
            translateHTMLEntitiesForComparison(editorContent)
        );
    }

    checkMetadataOnContract(blockId, content, forceUpdate = false) {
        const { builderUiStore } = this.rootStore;
        const block = builderUiStore.selectedFlow.draftRevision.blocksMap[
            blockId
        ] as ContractHTMLBlock;

        const contractMetadata = [];
        const domSmartFieldsID = [];
        const elem = document.createElement('div');
        elem.innerHTML = content;
        const smartFieldsElem = elem.querySelectorAll(
            'span[class*="hb-smart-field"]'
        );
        const smartBlocksElem = elem.querySelectorAll(
            'div[class*="hb-smart-box"]'
        );
        [...smartFieldsElem, ...smartBlocksElem].forEach(smartField => {
            domSmartFieldsID.push(smartField.getAttribute('data-field-id'));
        });
        const currMetaData = (block as ContractHTMLBlock).fieldsMap;

        let isMetadataChanged = false;
        Object.keys(currMetaData).forEach(smartField => {
            const fieldID = domSmartFieldsID.find(id => id === smartField);
            const field = currMetaData[fieldID];
            if (!field) {
                isMetadataChanged = true;
            } else {
                contractMetadata.push(field);
            }
        });

        if (forceUpdate || isMetadataChanged) {
            const oldContract = {
                ...(block as ContractHTMLBlock).contract
            };
            block.contract.fields_metadata = contractMetadata;

            this.updateBlock(
                block,
                {
                    contract: {
                        html_body: content,
                        fields_metadata: contractMetadata
                    }
                },
                {
                    oldData: { contract: oldContract }
                }
            );
        }
    }

    getDeletePageOrBlockSideEffectCommands(
        pageOrBlock: Page | TBlock,
        options: IDeleteBlockOptions = DefaultDeleteBlockOptions
    ) {
        const commands: IBasicCommandsActionValue = {
            commands: [],
            revertCommands: []
        };

        mergeArrayFields(
            commands,
            this.getCoupledBlocksDeleteCommands(pageOrBlock, options),
            this.getDisableServiceAssetsCommands(pageOrBlock)
        );

        this.cancelSessionIfNeeded(pageOrBlock);

        return commands;
    }

    private getCoupledBlocksDeleteCommands(
        pageOrBlock: Page | TBlock,
        options: IDeleteBlockOptions = DefaultDeleteBlockOptions
    ) {
        const commands: IBasicCommandsActionValue = {
            commands: [],
            revertCommands: []
        };

        pageOrBlock.coupledBlocks.forEach(coupledBlock => {
            mergeArrayFields(
                commands,
                deleteBlock(coupledBlock, this.rootStore, options)
            );
        });

        return commands;
    }

    private getDisableServiceAssetsCommands(pageOrBlock: Page | TBlock) {
        const commands: IBasicCommandsActionValue = {
            commands: [],
            revertCommands: []
        };

        const { flow } = pageOrBlock;

        if (flow.isLeadForm && pageOrBlock.isInvoiceOrPay) {
            flow.chargeableDigitalProductServices.forEach(serviceItem => {
                commands.commands.push(
                    createUpdateBlockCommand(serviceItem.block_id, {
                        service_asset_enabled: false
                    })
                );
                commands.revertCommands.push(
                    createUpdateBlockCommand(serviceItem.block_id, {
                        service_asset_enabled: true
                    })
                );
            });
        }

        return commands;
    }

    private cancelSessionIfNeeded(pageOrBlock: Page | TBlock) {
        const { variableCtrl } = this.rootStore;
        const { flow } = pageOrBlock;

        if (
            pageOrBlock.isSessionSelection &&
            flow.scheduleContainerBlock?.isScheduled
        ) {
            variableCtrl.resetScheduleVariable(flow.scheduleContainerBlock);
        }
    }
    private createMoveBlocksCommands(action: Action<IMoveBlockContext>) {
        const {
            blockId,
            movedChildrenNewParents,
            movedChildrenOldParents,
            flow
        } = action.ctx;

        const newParentId = Object.values(movedChildrenNewParents)[0];
        const oldParentId = Object.values(movedChildrenOldParents)[0];
        const commands = createMoveBlockCommands(
            flow,
            blockId,
            newParentId,
            action,
            action.ctx.order
        );

        const revertCommands = createRevertMoveBlockCommands(
            flow,
            blockId,
            newParentId,
            oldParentId
        );

        action.setContext({ commands, revertCommands });
    }

    private createBlockDuplicateCommands(
        action: Action<IDuplicateBlockContext>
    ) {
        const { ctx } = action;

        // prepare data
        const blockToDuplicate = ctx.flow.draftRevision.blocksMap[ctx.blockId];
        const order = getNewBlockOrder(blockToDuplicate, 'bottom');
        const commands = duplicateBlock(
            blockToDuplicate,
            this.rootStore,
            order,
            {
                ctx,
                withAttributions: true
            }
        );

        const revertCommands = createRevertDuplicateBlock(
            blockToDuplicate,
            commands
        );

        action.setContext({ commands, revertCommands });
    }

    private replaceBlockWithClipBoardCommands(
        action: Action<IReplaceBlockFromClipContext>
    ) {
        const { ctx } = action;

        const { commands, revertCommands } = PasteBlockFromClipboard({
            block: ctx.clipboardBlock,
            parent: ctx.parent,
            order: ctx.order,
            rootStore: this.rootStore,
            options: {
                ctx,
                withAttributions: true
            },
            allRelevantBlocks: ctx.allRelevantBlocks,
            originalBlockToReplace: ctx.originalBlockToReplace
        });

        action.setContext({
            commands,
            revertCommands
        });
    }

    private replaceBlockWithTemplateCommands(
        action: Action<IReplaceBlockContext>
    ) {
        const { ctx } = action;

        const { commands, revertCommands } = UpdateBlockToTemplate(
            ctx.templateBlock,
            ctx.parent,
            ctx.blockType,
            ctx.order,
            ctx.templateId,
            this.rootStore,
            {
                ctx
            }
        );

        action.setContext({ commands, revertCommands });
    }

    // #endregion

    // #region actions get server data

    private getBulkUpdateBlockDataForServer(
        action: Action<IBulkUpdateBlockContext>
    ) {
        const updated_data = action.ctx.data.map(
            (blockToUpdate: Partial<TBlock>) => ({
                block_id: blockToUpdate.block_id,
                ...blockToUpdate
            })
        );

        return {
            ...action.getBasicDataForServer(),
            page_id: action.ctx.pageId,
            updated_blocks: updated_data
        };
    }

    private getBulkRevertBlockDataForServer(
        action: Action<IBulkUpdateBlockContext>
    ) {
        const reverted_data = action.ctx.oldData.map(
            (blockToRevert: Partial<TBlock>) => ({
                block_id: blockToRevert.block_id,
                ...blockToRevert
            })
        );

        return {
            ...action.getBasicDataForServer(),
            page_id: action.ctx.pageId,
            updated_blocks: reverted_data
        };
    }

    private getBaseDataForServer(action: Action<IBlockBaseContext>) {
        const {
            ctx: { commands }
        } = action;

        return calculateBlockBaseDataForServer(commands, action);
    }

    private getAttributedDataForServer(action: Action<IAttributionContext>) {
        const {
            ctx: { commands }
        } = action;

        return calculatePageWithAttributionDataForServer(commands, action);
    }

    private getRevertBaseDataForServer(action: Action<IBlockBaseContext>) {
        const {
            ctx: { revertCommands }
        } = action;

        return calculateBlockBaseDataForServer(revertCommands, action);
    }

    ensureNotInMultiStageCreate(block: TBlock) {
        const { actionsManager, builderUiStore } = this.rootStore;

        let nextSidebarScreen = null;
        let action = actionsManager.getActionByIdParam({
            itemId: block.block_id,
            pageId: block.page_id,
            actionName: ActionName.CreateCompositionBlock
        });

        if (action) {
            nextSidebarScreen =
                builderUiStore.selectedSidebarScreenConfig.parent;
        }

        if (!action) {
            action = actionsManager.getActionByIdParam({
                itemId: block.block_id,
                pageId: block.page_id,
                actionName: ActionName.CreateQuestionBlock
            });

            if (action) {
                nextSidebarScreen = QuestionSidebarScreen.SelectedQuestion;
            }
        }

        if (!action) {
            action = actionsManager.getActionByIdParam({
                itemId: block.block_id,
                pageId: block.page_id,
                actionName: ActionName.CreateBlock
            });
        }

        if (!action) {
            return;
        }

        if (nextSidebarScreen) {
            builderUiStore.setSelectedSidebarScreen(nextSidebarScreen);
        }

        actionsManager.finishMultiStageAction(action);
    }

    private createPackagesBlockCreateCommand(
        newAction: Action<ICreatePackageBlockContext>
    ): void {
        const { ctx } = newAction;

        const totalCommands: TCommand[] = [];
        const totalRevertCommands: TCommand[] = [];
        const childrenLayoutArray: IChildLayout[] = [];

        ctx.services?.forEach((service: IServiceResponse, index: number) => {
            const { newBlocksMap, newChildLayout } =
                this.createInvoiceServiceItemBlock(ctx, service, index);
            childrenLayoutArray.push(newChildLayout);

            const commands: TCommand[] = Object.values(newBlocksMap).map(
                newBlock => createCreateBlockCommand(newBlock)
            );

            const revertCommands: TCommand[] = Object.keys(newBlocksMap).map(
                x => createDeleteBlockCommand(x)
            );

            totalCommands.push(...commands);
            totalRevertCommands.push(...revertCommands);
        });
        const parentOldChildrenLayout = [
            ...(ctx.block as ContainerableBlock).sortedChildLayouts
        ];

        totalCommands.push(
            createUpdateBlockCommand(ctx.block.block_id, {
                children: [...parentOldChildrenLayout, ...childrenLayoutArray]
            })
        );

        const {
            service_item: serviceItem,
            service_image,
            service_asset
        } = esServiceToServiceItem(ctx.item);

        const emptyInvoiceServiceItem = ctx.block.isServiceSelectionItemBlock
            ? { invoice_service_item: null }
            : {};

        const updatedServiceItem = createUpdateBlockCommand(
            ctx.block.block_id,
            {
                ...{
                    service_item: {
                        ...(ctx.block as ServiceItemBlock).service_item,
                        ...serviceItem
                    },
                    service_type: ctx.item.type,
                    service_image,
                    service_asset
                },
                ...emptyInvoiceServiceItem
            },
            { dataHandleMethod: DataHandleMethod.replace }
        );

        totalCommands.push(updatedServiceItem);

        const shouldExtractInvoiceServiceItem = ctx.block
            .isServiceSelectionItemBlock
            ? { invoice_service_item: true }
            : {};

        totalRevertCommands.push(
            createUpdateBlockCommand(
                ctx.block.block_id,
                extractOldData(
                    {
                        ...{
                            service_item: true,
                            service_type: true,
                            service_image: true,
                            service_asset: true
                        },
                        ...shouldExtractInvoiceServiceItem
                    },
                    ctx.block
                ),
                { dataHandleMethod: DataHandleMethod.replace }
            )
        );
        totalRevertCommands.push(
            createUpdateBlockCommand(ctx.block.block_id, {
                children: parentOldChildrenLayout
            })
        );

        newAction.setContext({
            commands: totalCommands,
            revertCommands: totalRevertCommands,
            updatedServiceItem: serviceItem
        });
    }

    private createPackagesBlockFromTemplateCreateCommand(
        newAction: Action<ICreatePackageBlockFromTemplateContext>,
        withTaxes = false
    ) {
        const { ctx } = newAction;

        const totalCommands: TCommand[] = [];
        const totalRevertCommands: TCommand[] = [];

        const serviceItem: IService = {
            quantity: ctx.item.quantity,
            description: ctx.item.description,
            title: ctx.item.title,
            tax_1: withTaxes && ctx.item.tax_1,
            tax_2: withTaxes && ctx.item.tax_2,
            tax_3: withTaxes && ctx.item.tax_3,
            price_per_unit: ctx.item.price_per_unit,
            price: ctx.item.price_per_unit * ctx.item.quantity,
            unit: ctx.item.unit,
            service_template_id: ctx.item._id,
            qb_item_id: ctx.item.qb_item_id
        };

        const emptyInvoiceServiceItem = ctx.block.isServiceSelectionItemBlock
            ? { invoice_service_item: null }
            : {};

        const updatedServiceItem = createUpdateBlockCommand(
            ctx.block.block_id,
            {
                ...{
                    service_item: {
                        ...(ctx.block as ServiceItemBlock).service_item,
                        ...serviceItem
                    },
                    service_image: ctx.serviceImage
                },
                ...emptyInvoiceServiceItem
            },
            { dataHandleMethod: DataHandleMethod.replace }
        );

        totalCommands.push(updatedServiceItem);

        const shouldExtractInvoiceServiceItem = ctx.block
            .isServiceSelectionItemBlock
            ? { invoice_service_item: true }
            : {};

        totalRevertCommands.push(
            createUpdateBlockCommand(
                ctx.block.block_id,
                extractOldData(
                    {
                        ...{
                            service_item: true,
                            service_type: true,
                            service_image: true
                        },
                        ...shouldExtractInvoiceServiceItem
                    },
                    ctx.block
                ),
                { dataHandleMethod: DataHandleMethod.replace }
            )
        );

        newAction.setContext({
            commands: totalCommands,
            revertCommands: totalRevertCommands,
            updatedServiceItem: serviceItem
        });
    }

    private createInvoiceServiceItemBlock(
        ctx: ICreatePackageBlockContext,
        service: IServiceResponse,
        orderIndex: number
    ) {
        const { service_item, service_image } = esServiceToServiceItem(service);

        const { newBlocksMap, newChildLayout } = createBlock(
            ctx.block as ContainerBlock,
            ctx.scope === ServiceItemScope.Invoice
                ? 'invoice_service_item_block'
                : 'service_selection_item_block',
            orderIndex,
            this.rootStore,
            {
                service_type: ServiceItemType.Service,
                service_item,
                service_image
            }
        );

        return { newBlocksMap, newChildLayout };
    }

    private getNonOverridablePropsData(
        block: TServiceItemBlock,
        serviceItem: Partial<IService>,
        serviceItemBlock: ServiceSelectionItemBlock
    ): Partial<IService> {
        // in case of none overridable props we want to update the service item and keep the original overridable props
        // if we are updating the SS item in invoice or isolated area we dont want to exclude the title and description
        const updateData = {
            service_item: {
                ...serviceItem,
                ...getServiceItemOverridableProps(
                    (block as ServiceSelectionItemBlock).service_item
                )
            }
        };
        // update invoice service item with the service item data and preserve the overridable invoice service item data
        if (serviceItemBlock.invoice_service_item) {
            // update service item for invoice presentation if needed
            // @ts-ignore
            updateData.invoice_service_item = {
                ...serviceItem,
                ...getServiceItemOverridableProps(
                    (block as ServiceSelectionItemBlock).invoice_service_item
                )
            };
        }

        return updateData as Partial<IService>;
    }

    toggleInvoiceType(
        invoiceBlock: InvoiceBlock,
        invoiceType: TInvoiceTypes.Recurring | TInvoiceTypes.PaymentSchedule,
        doNotSwitchToPaymentScheduleBlock?: boolean
    ) {
        const {
            actionsManager,
            builderUiStore: {
                selectBlock,
                selectedBlock,
                selectedFlow: { chargeBlock }
            },
            userStore: { currUser }
        } = this.rootStore;

        const isTurnRecurringOn = invoiceType === TInvoiceTypes.Recurring;

        let block = null;
        let blockType = null;

        if (isTurnRecurringOn) {
            block = invoiceBlock.invoicePaymentScheduleBlock;
            blockType = 'recurring_schedule_block';
        } else {
            block = invoiceBlock.invoiceRecurringScheduleBlock;
            blockType = 'payment_schedule_block';
        }

        actionsManager.groupActions((action: FlowActionGroup) => {
            return {
                name: ActionName.ToggleInvoiceType,
                serverActionType: ServerActionType.UpdateBlockToTemplate,
                applySideEffects: () => {
                    if (
                        doNotSwitchToPaymentScheduleBlock ||
                        (!isTurnRecurringOn &&
                            selectedBlock.isInvoiceContainerBlock)
                    ) {
                        selectBlock(selectedBlock.block_id);
                    }
                },
                onApplyToServerResolved: (
                    result,
                    { variables: newVariables }
                ) => this.addBlockVariables(action, newVariables),
                onRevertToServerResolved: () =>
                    this.removeBlockVariables(action),
                groupActions: () => {
                    this.updateBlock(invoiceBlock, {
                        invoice_type: invoiceType
                    });

                    this.replaceBlockWithBlock(block, blockType);
                    this.updateBlock(chargeBlock, {
                        allow_autopay:
                            isTurnRecurringOn &&
                            currUser.account.card_on_file_enabled,
                        allow_gratuity: false,
                        gratuity_type: 'all_payments'
                    });
                }
            };
        });
    }

    updateServiceBlockColumns({
        block,
        newLayout
    }: {
        block: ServiceSelectionBlock;
        newLayout: ServicesItemsLayoutType;
    }) {
        const { actionsManager } = this.rootStore;

        if (newLayout === block.services_layout) {
            return;
        }

        const defaultGapPerLayout = {
            [ServicesItemsLayoutType.Rows]: null,
            [ServicesItemsLayoutType.TwoColumns]: 40,
            [ServicesItemsLayoutType.ThreeColumns]: 20
        };

        actionsManager.groupActions(() => ({
            name: ActionName.UpdateServiceBlockColumns,
            serverActionType: ServerActionType.UpdateBlockColumns,
            groupActions: () => {
                this.updateServiceSelectionLayout(block, newLayout);

                if (defaultGapPerLayout[newLayout]) {
                    this.updateBlock(block, {
                        styles: { grid_gap: defaultGapPerLayout[newLayout] }
                    });
                }

                this.updateServiceItemsCropRatio(block, newLayout);
            }
        }));
    }

    updateScheduleSessionConfig({
        scheduleContainerBlock,
        sessionConfig
    }: {
        scheduleContainerBlock: ScheduleContainerBlock;
        sessionConfig: ISessionConfig;
    }) {
        const {
            actionsManager,
            variableCtrl,
            builderUiStore: { setShowScheduleOverlayForId }
        } = this.rootStore;

        setShowScheduleOverlayForId(null);

        actionsManager.groupActions(() => ({
            name: ActionName.UpdateBlockData,
            serverActionType: ServerActionType.UpdateBlock,
            groupActions: () => {
                this.updateBlock(scheduleContainerBlock, {
                    session_config_id: sessionConfig._id,
                    session_config: sessionConfig
                });
                if (scheduleContainerBlock.isScheduled) {
                    variableCtrl.resetScheduleVariable(scheduleContainerBlock);
                }
            }
        }));
    }

    updateSSItemSessionConfig({
        serviceSelectionItemBlock,
        sessionConfig
    }: {
        serviceSelectionItemBlock: ServiceSelectionItemBlock;
        sessionConfig: ISessionConfig;
    }) {
        const { actionsManager, variableCtrl } = this.rootStore;

        actionsManager.groupActions(() => ({
            name: ActionName.UpdateBlockData,
            serverActionType: ServerActionType.UpdateBlock,
            groupActions: () => {
                this.updateBlock(serviceSelectionItemBlock, {
                    session_config_id: sessionConfig._id,
                    session_config: sessionConfig
                });

                const scheduleContainerBlock =
                    serviceSelectionItemBlock.flow.scheduleContainerBlock;

                if (
                    serviceSelectionItemBlock.serviceSelectionBlock
                        ?.firstSelectedService?.block_id ==
                        serviceSelectionItemBlock.block_id &&
                    scheduleContainerBlock.isScheduled
                ) {
                    variableCtrl.resetScheduleVariable(scheduleContainerBlock);
                }
            }
        }));
    }

    toggleScheduleSessionTrigger({
        scheduleContainerBlock,
        triggerType,
        invoiceExistsApplySideEffect,
        invoiceExistsRevertSideEffect,
        invoiceCreatedApplySideEffect,
        invoiceCreatedRevertSideEffect
    }: {
        scheduleContainerBlock: ScheduleContainerBlock;
        triggerType: TScheduleTriggerType;
        invoiceExistsApplySideEffect?: () => void;
        invoiceExistsRevertSideEffect?: () => void;
        invoiceCreatedApplySideEffect?: () => void;
        invoiceCreatedRevertSideEffect?: () => void;
    }) {
        const { pageCtrl, actionsManager } = this.rootStore;

        const { hasInvoiceBlock } = scheduleContainerBlock.flowRevision;

        const isRequireToPay = triggerType === 'once_invoice_paid';

        const shouldCreateInvoice = isRequireToPay && !hasInvoiceBlock;

        const updateTrigger = (options?: IUpdateBlockOptions) => {
            this.updateBlock(
                scheduleContainerBlock,
                { schedule_session_trigger: triggerType },
                options
            );
        };

        if (!shouldCreateInvoice) {
            updateTrigger({
                applySideEffectsCallback: invoiceExistsApplySideEffect,
                revertSideEffectsCallback: invoiceExistsRevertSideEffect
            });
            return;
        }

        // group update with adding an invoice in a new page
        actionsManager.groupActions(() => ({
            name: ActionName.CreateBlankInvoice,
            serverActionType: ServerActionType.CreatePage,
            applySideEffects: invoiceCreatedApplySideEffect,
            revertSideEffects: invoiceCreatedRevertSideEffect,
            groupActions: () => {
                updateTrigger();

                pageCtrl.createPageAndBlockRelativeToPage({
                    relativeToPage: scheduleContainerBlock.page,
                    blockType: 'invoice_container_block',
                    pageTitle: 'Invoice',
                    blockOptions: { shouldSelectBlock: false }
                });
            }
        }));
    }

    toggleDigitalProductTrigger({
        serviceItemBlock,
        serviceItem,
        invoiceCreatedApplySideEffect,
        invoiceCreatedRevertSideEffect
    }: {
        serviceItemBlock: ServiceSelectionItemBlock;
        serviceItem: Partial<IService>;
        invoiceCreatedApplySideEffect?: () => void;
        invoiceCreatedRevertSideEffect?: () => void;
    }) {
        const { pageCtrl, actionsManager } = this.rootStore;

        const { hasInvoiceBlock } = serviceItemBlock.flowRevision;

        const shouldCreateInvoice =
            !hasInvoiceBlock &&
            serviceItemBlock.serviceSelectionBlock.isChargeable;

        const updateTrigger = () => {
            this.updateBlock(serviceItemBlock, {
                allow_custom_quantity: false,
                service_asset_enabled: true
            });
            this.updateServiceItemBlock(serviceItemBlock, {
                ...serviceItem,
                quantity: 1,
                unit: null
            });
        };

        if (!shouldCreateInvoice) {
            actionsManager.groupActions(() => ({
                name: ActionName.ToggleDigitalProduct,
                serverActionType: ServerActionType.UpdateBlock,
                groupActions: () => {
                    updateTrigger();
                }
            }));
            return;
        }

        // group update with adding an invoice in a new page
        actionsManager.groupActions(() => ({
            name: ActionName.CreateBlankInvoice,
            serverActionType: ServerActionType.CreatePage,
            applySideEffects: invoiceCreatedApplySideEffect,
            revertSideEffects: invoiceCreatedRevertSideEffect,
            groupActions: () => {
                updateTrigger();

                pageCtrl.createPageAndBlockRelativeToPage({
                    relativeToPage: null,
                    blockType: 'invoice_container_block',
                    pageTitle: 'Invoice',
                    blockOptions: { shouldSelectBlock: false }
                });
            }
        }));
    }

    changeServiceSelectionType({
        serviceSelectionBlock,
        newSelectionType,
        invoiceCreatedApplySideEffect,
        invoiceCreatedRevertSideEffect
    }: {
        serviceSelectionBlock: ServiceSelectionBlock;
        newSelectionType: ServiceSelectionType;
        invoiceCreatedApplySideEffect?: () => void;
        invoiceCreatedRevertSideEffect?: () => void;
    }) {
        const { pageCtrl, actionsManager } = this.rootStore;

        const { attachedInvoice } = serviceSelectionBlock;
        let selectedServicesChanged = false;

        if (
            serviceSelectionBlock.selection_type ===
                ServiceSelectionType.NoSelection ||
            newSelectionType === ServiceSelectionType.NoSelection
        ) {
            selectedServicesChanged = true;
        }

        if (selectedServicesChanged) {
            attachedInvoice?.invoiceSummaryBlock.setIsVariablesUpdated(false);
        }

        const updateTrigger = () => {
            this.updateBlock(
                serviceSelectionBlock,
                {
                    selection_type: newSelectionType,
                    send_info_to_invoice: false,
                    banner_text: getBannerDefaultText(
                        newSelectionType,
                        serviceSelectionBlock.isSessionSelection
                    )
                },
                {
                    applyResolvedCallback: () => {
                        if (selectedServicesChanged) {
                            attachedInvoice?.invoiceSummaryBlock.setIsVariablesUpdated(
                                true
                            );
                        }
                    }
                }
            );
        };

        const { hasInvoiceBlock } = serviceSelectionBlock.flowRevision;

        const shouldCreateInvoice =
            !hasInvoiceBlock &&
            !serviceSelectionBlock.isChargeable &&
            serviceSelectionBlock.hasDigitalProductServiceItems;

        if (!shouldCreateInvoice) {
            updateTrigger();
            return;
        }

        // group update with adding an invoice in a new page
        actionsManager.groupActions(() => ({
            name: ActionName.CreateBlankInvoice,
            serverActionType: ServerActionType.CreatePage,
            applySideEffects: invoiceCreatedApplySideEffect,
            revertSideEffects: invoiceCreatedRevertSideEffect,
            groupActions: () => {
                updateTrigger();

                pageCtrl.createPageAndBlockRelativeToPage({
                    relativeToPage: null,
                    blockType: 'invoice_container_block',
                    pageTitle: 'Invoice',
                    blockOptions: { shouldSelectBlock: false }
                });
            }
        }));
    }

    private updateServiceItemsCropRatio(
        serviceSelectionBlock: ServiceSelectionBlock,
        newLayout: ServicesItemsLayoutType
    ) {
        const { blockCtrl } = this.rootStore;

        let newCropRatio: TCropRatio;
        let blocksToUpdate: Array<ServiceSelectionItemBlock>;

        if (newLayout === ServicesItemsLayoutType.Rows) {
            newCropRatio = '1:1';
            blocksToUpdate = serviceSelectionBlock.allServiceItems;
        } else {
            newCropRatio = '4:3';
            blocksToUpdate = serviceSelectionBlock.serviceItems;
        }

        blocksToUpdate.forEach(serviceSelectionItemBlock => {
            if (
                !serviceSelectionItemBlock.service_image ||
                serviceSelectionItemBlock.service_image.crop_ratio ===
                    newCropRatio
            ) {
                return;
            }

            blockCtrl.updateBlock(serviceSelectionItemBlock, {
                service_image: {
                    ...serviceSelectionItemBlock.service_image,
                    crop_ratio: newCropRatio
                }
            });
        });
    }
}
